import type { ReactiveString, RequestOptions } from "../types/request";
import type { ServiceName } from "../types/service"

/**
 * @typedef {Object} addApplicationFormData Application's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string} text - Text
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {string} type - Type (industry, market, category, product)
 */
export type addApplicationFormData = {
	parent_id?: string|null;
	text: string;
	status: Number;
	type: string;
};

/**
 * @typedef {Object} editApplicationFormData Application's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string=} text - Text
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {string=} type - Type (industry, market, category, product)
 */
export type editApplicationFormData = {
	parent_id?: string|null;
	text?: string;
	status?: Number;
	type?: string;
};

/**
 * @typedef {Object} addCatalogFormData Catalog data
 * @prop {string} organization_id - Organization's id this catalog belongs to.
 * @prop {Number} tengiva_catalog_code - Tengiva's catalog code
 * @prop {string} type - Catalog's type
 */
export type addCatalogFormData = {
	organization_id: string;
	tengiva_catalog_code: Number;
	type: string;
};

/**
 * @typedef {Object} viewOrganizationShowroomProductsCatalogFormData Catalog data
 * @prop {string} organization_id - Organization's id this catalog belongs to.
 */
export type viewOrganizationShowroomProductsCatalogFormData = {
	organization_id: string;
};

/**
 * @typedef {Object} editCatalogFormData Catalog data
 * @prop {string=} organization_id - Organization's id this catalog belongs to.
 * @prop {Number=} tengiva_catalog_code - Tengiva's catalog code
 * @prop {string=} type - Catalog's type
 */
export type editCatalogFormData = {
	organization_id?: string;
	tengiva_catalog_code?: Number;
	type?: string;
};

/**
 * @typedef {Object} addCertificationFormData Certification data
 * @prop {string|null=} parent_id - The id that belongs to the certification's parent (Can be NULL if the entry is at the root)
 * @prop {string} text - Certification's text
 * @prop {Number=} status - Certification's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 1)
 */
export type addCertificationFormData = {
	parent_id?: string|null;
	text: string;
	status?: Number;
};

/**
 * @typedef {Object} editCertificationFormData Certification data
 * @prop {string|null=} parent_id - The id that belongs to the certification's parent (Can be NULL if the entry is at the root)
 * @prop {string=} text - Certification's text
 * @prop {Number=} status - Certification's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 1)
 */
export type editCertificationFormData = {
	parent_id?: string|null;
	text?: string;
	status?: Number;
};

/**
 * @typedef {Object} addCertificationsTextileFormData Certification textile data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} certification_id - Certification's id that needs to be linked
 * @prop {string} file_id - Id of file from file manager
 * @prop {string=} since - Since when is this certification valid
 * @prop {string=} status - Certification textile's status (0, 1 Or 2, 0:Not Disclosed, 1:Active, 2: In Renewal. Defaults to 1)
 */
export type addCertificationsTextileFormData = {
	textile_id: string;
	certification_id: string;
	file_id: string;
	since?: string;
	status?: string;
};

/**
 * @typedef {Object} editCertificationsTextileFormData Certification textile data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} certification_id - Certification's id that needs to be linked
 * @prop {string=} file_id - Id of file from file manager
 * @prop {string=} since - Since when is this certification valid
 * @prop {string=} status - Certification textile's status (0, 1 Or 2, 0:Not Disclosed, 1:Active, 2: In Renewal. Defaults to 1)
 */
export type editCertificationsTextileFormData = {
	textile_id?: string;
	certification_id?: string;
	file_id?: string;
	since?: string;
	status?: string;
};

/**
 * @typedef {Object} listChemicalsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listChemicalsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addChemicalFormData Chemical's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {boolean} status - Status
 */
export type addChemicalFormData = {
	parent_id?: string|null;
	text: string;
	translation_slug?: string|null;
	status: boolean;
};

/**
 * @typedef {Object} editChemicalFormData Chemical's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {boolean=} status - Status
 */
export type editChemicalFormData = {
	parent_id?: string|null;
	text?: string;
	translation_slug?: string|null;
	status?: boolean;
};

/**
 * @typedef {Object} addChemicalsTextileFormData Chemical textile data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} chemical_id - Chemical's id that needs to be linked
 * @prop {string=} brand_name - Name of the brand
 * @prop {string=} precision_to_other - When 'other' is selected as a chemical, this text field needs to be filled as a way to get the chemical's text
 */
export type addChemicalsTextileFormData = {
	textile_id: string;
	chemical_id: string;
	brand_name?: string;
	precision_to_other?: string;
};

/**
 * @typedef {Object} editChemicalsTextileFormData Chemical textile data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} chemical_id - Chemical's id that needs to be linked
 * @prop {string=} brand_name - Name of the brand
 * @prop {string=} precision_to_other - When 'other' is selected as a chemical, this text field needs to be filled as a way to get the chemical's text
 */
export type editChemicalsTextileFormData = {
	textile_id?: string;
	chemical_id?: string;
	brand_name?: string;
	precision_to_other?: string;
};

/**
 * @typedef {Object} listCollectionUsersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listCollectionUsersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addCollectionUserFormData Collection User's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string} collection_id - Collection Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string} user_email - User Email
 * @prop {string|null=} user_name - User Name
 * @prop {boolean} private_access - Private Access
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {Number|null=} revoked - Revoked
 * @prop {string|null=} revoker - Revoker
 */
export type addCollectionUserFormData = {
	platform_id?: string|null;
	collection_id: string;
	client_organization_id?: string|null;
	user_id?: string|null;
	contact_id?: string|null;
	user_email: string;
	user_name?: string|null;
	private_access: boolean;
	status: Number;
	revoked?: Number|null;
	revoker?: string|null;
};

/**
 * @typedef {Object} editCollectionUserFormData Collection User's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string=} collection_id - Collection Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string=} user_email - User Email
 * @prop {string|null=} user_name - User Name
 * @prop {boolean=} private_access - Private Access
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {Number|null=} revoked - Revoked
 * @prop {string|null=} revoker - Revoker
 */
export type editCollectionUserFormData = {
	platform_id?: string|null;
	collection_id?: string;
	client_organization_id?: string|null;
	user_id?: string|null;
	contact_id?: string|null;
	user_email?: string;
	user_name?: string|null;
	private_access?: boolean;
	status?: Number;
	revoked?: Number|null;
	revoker?: string|null;
};

/**
 * @typedef {Object} addManyCollectionUserFormData
 * @prop {{}} users - list of emails to process
 * @prop {string} platform_id - platform_id the textile item belongs to
 * @prop {boolean} private_access - whether or not the users will have access to the product when it is private
 */
export type addManyCollectionUserFormData = {
	users: {};
	platform_id: string;
	private_access: boolean;
};

/**
 * @typedef {Object} listCollectionFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listCollectionFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addCollectionFormData Collection's data
 * @prop {string} organization_id - Organization Id
 * @prop {string} user_id - User Id
 * @prop {string} name - Name
 * @prop {boolean} visibility - Visibility
 * @prop {string|null=} description - Description
 * @prop {string|null=} icon_id - Icon Id
 * @prop {string|null=} slug - Slug
 * @prop {Number} status - Status (0, 1, 2, 99)
 * @prop {{}=} collections_textiles
 */
export type addCollectionFormData = {
	organization_id: string;
	user_id: string;
	name: string;
	visibility: boolean;
	description?: string|null;
	icon_id?: string|null;
	slug?: string|null;
	status: Number;
	collections_textiles?: {};
};

/**
 * @typedef {Object} editCollectionFormData Collection's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string=} user_id - User Id
 * @prop {string=} name - Name
 * @prop {boolean=} visibility - Visibility
 * @prop {string|null=} description - Description
 * @prop {string|null=} icon_id - Icon Id
 * @prop {string|null=} slug - Slug
 * @prop {Number=} status - Status (0, 1, 2, 99)
 */
export type editCollectionFormData = {
	organization_id?: string;
	user_id?: string;
	name?: string;
	visibility?: boolean;
	description?: string|null;
	icon_id?: string|null;
	slug?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} addCollectionsTextileItemFormData Collection textile item data
 * @prop {string} collection_id - Collection's id that needs to be linked
 * @prop {string} collections_textile_id - Collections_textile's id that needs to be linked
 * @prop {string} textile_item_id - textiles_item's id that needs to be linked
 * @prop {boolean=} visibility - Whether or not the collections_textile_item is visible (Defaults to 1)
 */
export type addCollectionsTextileItemFormData = {
	collection_id: string;
	collections_textile_id: string;
	textile_item_id: string;
	visibility?: boolean;
};

/**
 * @typedef {Object} editCollectionsTextileItemFormData Collection textile item data
 * @prop {string=} collection_id - Collection's id that needs to be linked
 * @prop {string=} collections_textile_id - Collections_textile's id that needs to be linked
 * @prop {string=} textile_item_id - textiles_item's id that needs to be linked
 * @prop {boolean=} visibility - Whether or not the collections_textile_item is visible (Defaults to 1)
 */
export type editCollectionsTextileItemFormData = {
	collection_id?: string;
	collections_textile_id?: string;
	textile_item_id?: string;
	visibility?: boolean;
};

/**
 * @typedef {Object} addCollectionsTextileFormData Collection textile data
 * @prop {string} collection_id - Collection's id that needs to be linked
 * @prop {string} textile_id - Textile's id that needs to be linked
 */
export type addCollectionsTextileFormData = {
	collection_id: string;
	textile_id: string;
};

/**
 * @typedef {Object} addManyCollectionsTextileFormData Collection textile data
 * @prop {string} collection_id - Collection's id that needs to be linked
 * @prop {string} organization_id - id of the organization that owns this collection.
 * @prop {{}=} collections_textiles
 */
export type addManyCollectionsTextileFormData = {
	collection_id: string;
	organization_id: string;
	collections_textiles?: {};
};

/**
 * @typedef {Object} editCollectionsTextileFormData Collection textile data
 * @prop {string=} collection_id - Collection's id that needs to be linked
 * @prop {string=} textile_id - Textile's id that needs to be linked
 */
export type editCollectionsTextileFormData = {
	collection_id?: string;
	textile_id?: string;
};

/**
 * @typedef {Object} addColorCoordinatesTextilesItemFormData Color coordinate textile item data
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} color_id - Color's id that needs to be linked
 * @prop {Number} l - l value of the color coordinate
 * @prop {Number} a - a value of the color coordinate
 * @prop {Number} b - b value of the color coordinate
 * @prop {Number} number - the value of number dictates the order in which the entry will be shown
 */
export type addColorCoordinatesTextilesItemFormData = {
	textile_item_id: string;
	color_id?: string;
	l: Number;
	a: Number;
	b: Number;
	number: Number;
};

/**
 * @typedef {Object} editColorCoordinatesTextilesItemFormData Color coordinate textile item data
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} color_id - Color's id that needs to be linked
 * @prop {Number=} l - l value of the color coordinate
 * @prop {Number=} a - a value of the color coordinate
 * @prop {Number=} b - b value of the color coordinate
 * @prop {Number=} number - the value of number dictates the order in which the entry will be shown
 */
export type editColorCoordinatesTextilesItemFormData = {
	textile_item_id?: string;
	color_id?: string;
	l?: Number;
	a?: Number;
	b?: Number;
	number?: Number;
};

/**
 * @typedef {Object} listColorPatternsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listColorPatternsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addColorPatternFormData Color Pattern's data
 * @prop {string} name - Name
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type addColorPatternFormData = {
	name: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} editColorPatternFormData Color Pattern's data
 * @prop {string=} name - Name
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type editColorPatternFormData = {
	name?: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} listColorSpacesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listColorSpacesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addColorSpaceFormData Color Space's data
 * @prop {string} icon_id - Icon id
 * @prop {string|null=} name - Name
 * @prop {string|null=} common_name - Common Name
 * @prop {string|null=} name_translation_slug - Name Translation Slug
 * @prop {string|null=} tengiva_internal_name - Tengiva Internal Name
 * @prop {string|null=} description - Description
 * @prop {string|null=} description_translation_slug - Description Translation Slug
 * @prop {string|null=} tengiva_internal_description - Tengiva Internal Description
 * @prop {Number=} status - Status (0, 1, 50, 99)
 */
export type addColorSpaceFormData = {
	icon_id: string;
	name?: string|null;
	common_name?: string|null;
	name_translation_slug?: string|null;
	tengiva_internal_name?: string|null;
	description?: string|null;
	description_translation_slug?: string|null;
	tengiva_internal_description?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} editColorSpaceFormData Color Space's data
 * @prop {string=} icon_id - Icon id
 * @prop {string|null=} name - Name
 * @prop {string|null=} common_name - Common Name
 * @prop {string|null=} name_translation_slug - Name Translation Slug
 * @prop {string|null=} tengiva_internal_name - Tengiva Internal Name
 * @prop {string|null=} description - Description
 * @prop {string|null=} description_translation_slug - Description Translation Slug
 * @prop {string|null=} tengiva_internal_description - Tengiva Internal Description
 * @prop {Number=} status - Status (0, 1, 50, 99)
 */
export type editColorSpaceFormData = {
	icon_id?: string;
	name?: string|null;
	common_name?: string|null;
	name_translation_slug?: string|null;
	tengiva_internal_name?: string|null;
	description?: string|null;
	description_translation_slug?: string|null;
	tengiva_internal_description?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} listColorTypesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listColorTypesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addColorTypeFormData Color Type's data
 * @prop {string} name - Name
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type addColorTypeFormData = {
	name: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} editColorTypeFormData Color Type's data
 * @prop {string=} name - Name
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type editColorTypeFormData = {
	name?: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} listColorsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listColorsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addColorFormData Color's data
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type addColorFormData = {
	text: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} editColorFormData Color's data
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type editColorFormData = {
	text?: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} addColortypesTextilesItemFormData Colortype textile item data
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {string} color_type_id - Color type's id that needs to be linked
 * @prop {string} color_pattern_id - Color pattern's id that needs to be linked
 */
export type addColortypesTextilesItemFormData = {
	textile_item_id: string;
	color_type_id: string;
	color_pattern_id: string;
};

/**
 * @typedef {Object} editColortypesTextilesItemFormData Colortype textile item data
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} color_type_id - Color type's id that needs to be linked
 * @prop {string=} color_pattern_id - Color pattern's id that needs to be linked
 */
export type editColortypesTextilesItemFormData = {
	textile_item_id?: string;
	color_type_id?: string;
	color_pattern_id?: string;
};

/**
 * @typedef {Object} listConstructionAddonsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listConstructionAddonsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addConstructionAddonFormData Construction Addon's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type addConstructionAddonFormData = {
	parent_id?: string|null;
	text: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} editConstructionAddonFormData Construction Addon's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type editConstructionAddonFormData = {
	parent_id?: string|null;
	text?: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} addConstructionAddonsLayerFormData Construction addon layer data
 * @prop {string} construction_addon_id - Construction addon's id that needs to be linked
 * @prop {string} layer_id - Layer's id that needs to be linked
 */
export type addConstructionAddonsLayerFormData = {
	construction_addon_id: string;
	layer_id: string;
};

/**
 * @typedef {Object} editConstructionAddonsLayerFormData Construction addon layer data
 * @prop {string=} construction_addon_id - Construction addon's id that needs to be linked
 * @prop {string=} layer_id - Layer's id that needs to be linked
 */
export type editConstructionAddonsLayerFormData = {
	construction_addon_id?: string;
	layer_id?: string;
};

/**
 * @typedef {Object} addConstructionFormData Construction data
 * @prop {string|null=} parent_id - The id that belongs to the construction's parent (Can be NULL if the entry is at the root)
 * @prop {string} text - Construction's text
 * @prop {Number=} status - Construction's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 1)
 */
export type addConstructionFormData = {
	parent_id?: string|null;
	text: string;
	status?: Number;
};

/**
 * @typedef {Object} editConstructionFormData Construction data
 * @prop {string|null=} parent_id - The id that belongs to the construction's parent (Can be NULL if the entry is at the root)
 * @prop {string=} text - Construction's text
 * @prop {Number=} status - Construction's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 1)
 */
export type editConstructionFormData = {
	parent_id?: string|null;
	text?: string;
	status?: Number;
};

/**
 * @typedef {Object} listDyeMethodsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listDyeMethodsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addDyeMethodFormData Dye Method's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type addDyeMethodFormData = {
	parent_id?: string|null;
	text: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} editDyeMethodFormData Dye Method's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type editDyeMethodFormData = {
	parent_id?: string|null;
	text?: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} addDyeMethodsTextilesItemFormData Dye method textile item data
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {string} dyemethod_id - Dye method's id that needs to be linked
 */
export type addDyeMethodsTextilesItemFormData = {
	textile_item_id: string;
	dyemethod_id: string;
};

/**
 * @typedef {Object} editDyeMethodsTextilesItemFormData Dye method textile item data
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} dyemethod_id - Dye method's id that needs to be linked
 */
export type editDyeMethodsTextilesItemFormData = {
	textile_item_id?: string;
	dyemethod_id?: string;
};

/**
 * @typedef {Object} listDyeStuffsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listDyeStuffsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addDyeStuffFormData Dye Stuff's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type addDyeStuffFormData = {
	parent_id?: string|null;
	text: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} editDyeStuffFormData Dye Stuff's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 */
export type editDyeStuffFormData = {
	parent_id?: string|null;
	text?: string;
	translation_slug?: string|null;
};

/**
 * @typedef {Object} addDyeStuffsTextileItemFormData Dye stuff textile item data
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {string} dye_stuff_id - Dye stuff's id that needs to be linked
 */
export type addDyeStuffsTextileItemFormData = {
	textile_item_id: string;
	dye_stuff_id: string;
};

/**
 * @typedef {Object} editDyeStuffsTextileItemFormData Dye stuff textile item data
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} dye_stuff_id - Dye stuff's id that needs to be linked
 */
export type editDyeStuffsTextileItemFormData = {
	textile_item_id?: string;
	dye_stuff_id?: string;
};

/**
 * @typedef {Object} listExistingTextileItemDefaultsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listExistingTextileItemDefaultsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addExistingTextileItemDefaultFormData Existing Textile Item Default's data
 * @prop {string} textile_production_order_default_id - Textile Production Order Default Id
 * @prop {string} existing_item_min_order_quantity_unit - Existing Item Min Order Quantity Unit
 * @prop {Number} existing_item_min_order_quantity_unit_amount - Existing Item Min Order Quantity Unit Amount
 * @prop {string} existing_item_avg_lead_time_unit - Existing Item Avg Lead Time Unit
 * @prop {Number} existing_item_avg_lead_time_unit_amount - Existing Item Avg Lead Time Unit Amount
 * @prop {Number} status - Status (0, 1, 50, 99)
 */
export type addExistingTextileItemDefaultFormData = {
	textile_production_order_default_id: string;
	existing_item_min_order_quantity_unit: string;
	existing_item_min_order_quantity_unit_amount: Number;
	existing_item_avg_lead_time_unit: string;
	existing_item_avg_lead_time_unit_amount: Number;
	status: Number;
};

/**
 * @typedef {Object} editExistingTextileItemDefaultFormData Existing Textile Item Default's data
 * @prop {string=} textile_production_order_default_id - Textile Production Order Default Id
 * @prop {string=} existing_item_min_order_quantity_unit - Existing Item Min Order Quantity Unit
 * @prop {Number=} existing_item_min_order_quantity_unit_amount - Existing Item Min Order Quantity Unit Amount
 * @prop {string=} existing_item_avg_lead_time_unit - Existing Item Avg Lead Time Unit
 * @prop {Number=} existing_item_avg_lead_time_unit_amount - Existing Item Avg Lead Time Unit Amount
 * @prop {Number=} status - Status (0, 1, 50, 99)
 * @prop {boolean=} propagation - if set to true, will also edit existing_textile_items data that are set to use default data.
 */
export type editExistingTextileItemDefaultFormData = {
	textile_production_order_default_id?: string;
	existing_item_min_order_quantity_unit?: string;
	existing_item_min_order_quantity_unit_amount?: Number;
	existing_item_avg_lead_time_unit?: string;
	existing_item_avg_lead_time_unit_amount?: Number;
	status?: Number;
	propagation?: boolean;
};

/**
 * @typedef {Object} listExistingTextileItemsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listExistingTextileItemsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addExistingTextileItemFormData Existing Textile Item's data
 * @prop {string} textile_production_order_requirement_id - Textile Production Order Requirement Id
 * @prop {string} textile_item_id - Textile Item Id
 * @prop {string} existing_item_min_order_quantity_unit - Existing Item Min Order Quantity Unit
 * @prop {Number} existing_item_min_order_quantity_unit_amount - Existing Item Min Order Quantity Unit Amount
 * @prop {string} existing_item_avg_lead_time_unit - Existing Item Avg Lead Time Unit
 * @prop {Number} existing_item_avg_lead_time_unit_amount - Existing Item Avg Lead Time Unit Amount
 * @prop {boolean} use_default - Use Default
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addExistingTextileItemFormData = {
	textile_production_order_requirement_id: string;
	textile_item_id: string;
	existing_item_min_order_quantity_unit: string;
	existing_item_min_order_quantity_unit_amount: Number;
	existing_item_avg_lead_time_unit: string;
	existing_item_avg_lead_time_unit_amount: Number;
	use_default: boolean;
	status: Number;
};

/**
 * @typedef {Object} editExistingTextileItemFormData Existing Textile Item's data
 * @prop {string=} textile_production_order_requirement_id - Textile Production Order Requirement Id
 * @prop {string=} textile_item_id - Textile Item Id
 * @prop {string=} existing_item_min_order_quantity_unit - Existing Item Min Order Quantity Unit
 * @prop {Number=} existing_item_min_order_quantity_unit_amount - Existing Item Min Order Quantity Unit Amount
 * @prop {string=} existing_item_avg_lead_time_unit - Existing Item Avg Lead Time Unit
 * @prop {Number=} existing_item_avg_lead_time_unit_amount - Existing Item Avg Lead Time Unit Amount
 * @prop {boolean=} use_default - Use Default
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editExistingTextileItemFormData = {
	textile_production_order_requirement_id?: string;
	textile_item_id?: string;
	existing_item_min_order_quantity_unit?: string;
	existing_item_min_order_quantity_unit_amount?: Number;
	existing_item_avg_lead_time_unit?: string;
	existing_item_avg_lead_time_unit_amount?: Number;
	use_default?: boolean;
	status?: Number;
};

/**
 * @typedef {Object} listFiberBrandsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listFiberBrandsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addFiberBrandFormData Fiber Brand's data
 * @prop {string} brand - Brand
 */
export type addFiberBrandFormData = {
	brand: string;
};

/**
 * @typedef {Object} editFiberBrandFormData Fiber Brand's data
 * @prop {string=} brand - Brand
 */
export type editFiberBrandFormData = {
	brand?: string;
};

/**
 * @typedef {Object} listFibersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listFibersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addFiberFormData Fiber's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string} text - Text
 * @prop {string|null=} text_translation_slug - Text Translation Slug
 * @prop {string|null=} short_description_translation_slug - Short Description Translation Slug
 * @prop {string} short_description - Short Description
 */
export type addFiberFormData = {
	parent_id?: string|null;
	text: string;
	text_translation_slug?: string|null;
	short_description_translation_slug?: string|null;
	short_description: string;
};

/**
 * @typedef {Object} editFiberFormData Fiber's data
 * @prop {string|null=} parent_id - Parent Id
 * @prop {string=} text - Text
 * @prop {string|null=} text_translation_slug - Text Translation Slug
 * @prop {string|null=} short_description_translation_slug - Short Description Translation Slug
 * @prop {string=} short_description - Short Description
 */
export type editFiberFormData = {
	parent_id?: string|null;
	text?: string;
	text_translation_slug?: string|null;
	short_description_translation_slug?: string|null;
	short_description?: string;
};

/**
 * @typedef {Object} addFibersLayerFormData Fiber layer data
 * @prop {string} layer_id - Layer's id that needs to be linked
 * @prop {string} fiber_id - Fiber's id that needs to be linked
 * @prop {string|null=} fiber_brand_id - Fiber brand's id that needs to be linked
 * @prop {Number} percentage - What percentage of this particular fiber this layer is composed of
 */
export type addFibersLayerFormData = {
	layer_id: string;
	fiber_id: string;
	fiber_brand_id?: string|null;
	percentage: Number;
};

/**
 * @typedef {Object} editFibersLayerFormData Fiber layer data
 * @prop {string=} layer_id - Layer's id that needs to be linked
 * @prop {string=} fiber_id - Fiber's id that needs to be linked
 * @prop {string|null=} fiber_brand_id - Fiber brand's is that needs to be linked
 * @prop {Number=} percentage - What percentage of this particular fiber this layer is composed of
 */
export type editFibersLayerFormData = {
	layer_id?: string;
	fiber_id?: string;
	fiber_brand_id?: string|null;
	percentage?: Number;
};

/**
 * @typedef {Object} addImagesTextilesItemFormData Image textile item data
 * @prop {string} textile_item_id - Textile item's id that needs to be linked. Every new row added will have this id as a textile_item_id
 * @prop {{}} images - Array that contains all the texilte_item's image information
 */
export type addImagesTextilesItemFormData = {
	textile_item_id: string;
	images: {};
};

/**
 * @typedef {Object} editImagesTextilesItemFormData Image textile item data
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} image_id - Id of the icon found in FM that will represent the textile item
 * @prop {string=} image_path - Name of the file on google cloud storage
 * @prop {Number=} number - the value of number dictates the order in which the entry will be shown
 */
export type editImagesTextilesItemFormData = {
	textile_item_id?: string;
	image_id?: string;
	image_path?: string;
	number?: Number;
};

/**
 * @typedef {Object} setImagePathImagesTextilesItemFormData
 * @prop {string} textile_item_id - Id of the textile item
 * @prop {string} image_id - Id of the record from ecm_odc_files table
 * @prop {string} image_path - Name of the file on google cloud storage
 */
export type setImagePathImagesTextilesItemFormData = {
	textile_item_id: string;
	image_id: string;
	image_path: string;
};

/**
 * @typedef {Object} listLayerNamesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listLayerNamesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addLayerNameFormData Layer Name's data
 * @prop {string} name - Name
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {string|null=} description - Description
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addLayerNameFormData = {
	name: string;
	translation_slug?: string|null;
	description?: string|null;
	status: Number;
};

/**
 * @typedef {Object} editLayerNameFormData Layer Name's data
 * @prop {string=} name - Name
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {string|null=} description - Description
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editLayerNameFormData = {
	name?: string;
	translation_slug?: string|null;
	description?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} listLayeringTypesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listLayeringTypesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addLayeringTypeFormData Layering Type's data
 * @prop {string} type - Type
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addLayeringTypeFormData = {
	type: string;
	translation_slug?: string|null;
	status: Number;
};

/**
 * @typedef {Object} editLayeringTypeFormData Layering Type's data
 * @prop {string=} type - Type
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editLayeringTypeFormData = {
	type?: string;
	translation_slug?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} addLayerFormData Layer data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {Number} sequence - Determines the order in which the layers appear in the frontend
 * @prop {string=} layering_type_id - Layering type's id that needs to be linked
 * @prop {string} construction_id - Construction's id that needs to be linked
 * @prop {string=} layer_name_id - Layer name's id that needs to be linked
 * @prop {Number=} yarn_count_weft - layer's weft
 * @prop {Number=} yarn_count_warp - layer's warp
 * @prop {string} yarn_size_number - yarn size's number
 * @prop {string} yarn_size_text - yarn size's text
 * @prop {string=} yarn_count_weft_unit - layer's weft unit
 * @prop {string=} yarn_count_warp_unit - layer's warp unit
 * @prop {string=} yarn_size_number_unit - layer's size number unit
 */
export type addLayerFormData = {
	textile_id: string;
	sequence: Number;
	layering_type_id?: string;
	construction_id: string;
	layer_name_id?: string;
	yarn_count_weft?: Number;
	yarn_count_warp?: Number;
	yarn_size_number: string;
	yarn_size_text: string;
	yarn_count_weft_unit?: string;
	yarn_count_warp_unit?: string;
	yarn_size_number_unit?: string;
};

/**
 * @typedef {Object} editLayerFormData Layer data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {Number=} sequence - Determines the order in which the layers appear in the frontend
 * @prop {string=} layering_type_id - Layering type's id that needs to be linked
 * @prop {string=} construction_id - Construction's id that needs to be linked
 * @prop {string=} layer_name_id - Layer name's id that needs to be linked
 * @prop {Number=} yarn_count_weft - layer's weft
 * @prop {Number=} yarn_count_warp - layer's warp
 * @prop {string=} yarn_size_number - yarn size's number
 * @prop {string=} yarn_size_text - yarn size's text
 * @prop {string=} yarn_count_weft_unit - layer's weft unit
 * @prop {string=} yarn_count_warp_unit - layer's warp unit
 * @prop {string=} yarn_size_number_unit - layer's size number unit
 */
export type editLayerFormData = {
	textile_id?: string;
	sequence?: Number;
	layering_type_id?: string;
	construction_id?: string;
	layer_name_id?: string;
	yarn_count_weft?: Number;
	yarn_count_warp?: Number;
	yarn_size_number?: string;
	yarn_size_text?: string;
	yarn_count_weft_unit?: string;
	yarn_count_warp_unit?: string;
	yarn_size_number_unit?: string;
};

/**
 * @typedef {Object} listLightSourcesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listLightSourcesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addLightSourceFormData Light Source's data
 * @prop {string} color_space_id - Color Space Id
 * @prop {string|null=} code - Code
 * @prop {string} color_temperature - Color Temperature
 * @prop {string|null=} color - Color
 * @prop {string} name - Name
 * @prop {string|null=} common_name - Common Name
 * @prop {string|null=} name_translation_slug - Name Translation Slug
 * @prop {string|null=} tengiva_internal_name - Tengiva Internal Name
 * @prop {string|null=} description - Description
 * @prop {string|null=} description_translation_slug - Description Translation Slug
 * @prop {string|null=} tengiva_internal_description - Tengiva Internal Description
 * @prop {Number=} status - Status (0, 1, 50, 99)
 */
export type addLightSourceFormData = {
	color_space_id: string;
	code?: string|null;
	color_temperature: string;
	color?: string|null;
	name: string;
	common_name?: string|null;
	name_translation_slug?: string|null;
	tengiva_internal_name?: string|null;
	description?: string|null;
	description_translation_slug?: string|null;
	tengiva_internal_description?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} editLightSourceFormData Light Source's data
 * @prop {string=} color_space_id - Color Space Id
 * @prop {string|null=} code - Code
 * @prop {string=} color_temperature - Color Temperature
 * @prop {string|null=} color - Color
 * @prop {string=} name - Name
 * @prop {string|null=} common_name - Common Name
 * @prop {string|null=} name_translation_slug - Name Translation Slug
 * @prop {string|null=} tengiva_internal_name - Tengiva Internal Name
 * @prop {string|null=} description - Description
 * @prop {string|null=} description_translation_slug - Description Translation Slug
 * @prop {string|null=} tengiva_internal_description - Tengiva Internal Description
 * @prop {Number=} status - Status (0, 1, 50, 99)
 */
export type editLightSourceFormData = {
	color_space_id?: string;
	code?: string|null;
	color_temperature?: string;
	color?: string|null;
	name?: string;
	common_name?: string|null;
	name_translation_slug?: string|null;
	tengiva_internal_name?: string|null;
	description?: string|null;
	description_translation_slug?: string|null;
	tengiva_internal_description?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} addLogbookFormData Logbook data
 * @prop {string} organization_id - Organization's id that needs to be linked
 */
export type addLogbookFormData = {
	organization_id: string;
};

/**
 * @typedef {Object} editLogbookFormData Logbook data
 * @prop {string=} organization_id - Organization's id that needs to be linked
 */
export type editLogbookFormData = {
	organization_id?: string;
};

/**
 * @typedef {Object} addLogentryFormData Textile logbooks entry data
 * @prop {string} logsheet_id - logsheet's id that needs to be linked
 * @prop {Number} version - Which version this entry is based on
 * @prop {string} summary - A brief summary of the logentry
 * @prop {string} table_updated_name - Which table was updated in this entry
 * @prop {string} action - If the data was added, updated, removed
 * @prop {string} field - The field's name that is impacted
 * @prop {string} new_data - The new data
 * @prop {string} old_data - The old data
 * @prop {string} begin_lead_time - at what time the version becomes valid
 * @prop {string} end_lead_time - at what time the version is no longer valid
 */
export type addLogentryFormData = {
	logsheet_id: string;
	version: Number;
	summary: string;
	table_updated_name: string;
	action: string;
	field: string;
	new_data: string;
	old_data: string;
	begin_lead_time: string;
	end_lead_time: string;
};

/**
 * @typedef {Object} editLogentryFormData Textile logbook entry data
 * @prop {string=} logsheet_id - logsheet's id that needs to be linked
 * @prop {Number=} version - Which version this entry is based on
 * @prop {string=} summary - A brief summary of the logentry
 * @prop {string=} table_updated_name - Which table was updated in this entry
 * @prop {string=} action - If the data was added, updated, removed
 * @prop {string=} field - The field's name that is impacted
 * @prop {string=} new_data - The new data
 * @prop {string=} old_data - The old data
 * @prop {string=} begin_lead_time - at what time the version becomes valid
 * @prop {string=} end_lead_time - at what time the version is no longer valid
 */
export type editLogentryFormData = {
	logsheet_id?: string;
	version?: Number;
	summary?: string;
	table_updated_name?: string;
	action?: string;
	field?: string;
	new_data?: string;
	old_data?: string;
	begin_lead_time?: string;
	end_lead_time?: string;
};

/**
 * @typedef {Object} addLogsheetFormData Logsheet data
 * @prop {string} logbook_id - Logbook's id that needs to be linked
 * @prop {string} product_id - Product's id that needs to be linked
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {string} current_version - Current version's number
 * @prop {string} current_entry_id - Id of the current entry
 */
export type addLogsheetFormData = {
	logbook_id: string;
	product_id: string;
	textile_id: string;
	textile_item_id: string;
	current_version: string;
	current_entry_id: string;
};

/**
 * @typedef {Object} editLogsheetFormData Logsheet data
 * @prop {string=} logbook_id - Logbook's id that needs to be linked
 * @prop {string=} product_id - Product's id that needs to be linked
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} current_version - Current version's number
 * @prop {string=} current_entry_id - Id of the current entry
 */
export type editLogsheetFormData = {
	logbook_id?: string;
	product_id?: string;
	textile_id?: string;
	textile_item_id?: string;
	current_version?: string;
	current_entry_id?: string;
};

/**
 * @typedef {Object} listProductsForMktplcMarketplaceFormData
 * @prop {string=} organization_id - ID of the merchant organization of the products to fetch.
 * @prop {{}=} filters - List of filters to apply on the results
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listProductsForMktplcMarketplaceFormData = {
	organization_id?: string;
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} listProductsByOrganizationForPlatformIdMarketplaceFormData Optional request data
 * @prop {string|null=} tabView - Page tab view
 */
export type listProductsByOrganizationForPlatformIdMarketplaceFormData = {
	tabView?: string|null;
};

/**
 * @typedef {Object} viewCollectionsItemsSummaryMarketplaceFormData
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewCollectionsItemsSummaryMarketplaceFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} listMoodboardsItemsSummaryMarketplaceFormData
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listMoodboardsItemsSummaryMarketplaceFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} getApplicableFiltersMarketplaceFormData
 * @prop {{}=} filters - List of filters to apply on the results
 */
export type getApplicableFiltersMarketplaceFormData = {
	filters?: {};
};

/**
 * @typedef {Object} listMechanicalsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listMechanicalsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addMechanicalFormData Mechanical's data
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addMechanicalFormData = {
	text: string;
	translation_slug?: string|null;
	status: Number;
};

/**
 * @typedef {Object} editMechanicalFormData Mechanical's data
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editMechanicalFormData = {
	text?: string;
	translation_slug?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} addMechanicalsTextileFormData Mechanical textile data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} mechanical_id - Mechanical's id that needs to be linked
 * @prop {string=} brand_name - Mechanical textile's brand name
 */
export type addMechanicalsTextileFormData = {
	textile_id: string;
	mechanical_id: string;
	brand_name?: string;
};

/**
 * @typedef {Object} editMechanicalsTextileFormData Mechanical textile data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} mechanical_id - Mechanical's id that needs to be linked
 * @prop {string=} brand_name - Mechanical textile's brand name
 */
export type editMechanicalsTextileFormData = {
	textile_id?: string;
	mechanical_id?: string;
	brand_name?: string;
};

/**
 * @typedef {Object} listMoodboardItemsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listMoodboardItemsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addMoodboardItemFormData Moodboard Item's data
 * @prop {string} moodboard_id - Moodboard Id
 * @prop {string} textile_id - Textile Id
 * @prop {string} textile_item_id - Textile Item Id
 * @prop {string|null=} comment - Comment
 * @prop {Number} selection_status - Selection Status (0, 1, 3)
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addMoodboardItemFormData = {
	moodboard_id: string;
	textile_id: string;
	textile_item_id: string;
	comment?: string|null;
	selection_status: Number;
	status: Number;
};

/**
 * @typedef {Object} editMoodboardItemFormData Moodboard Item's data
 * @prop {string=} moodboard_id - Moodboard Id
 * @prop {string=} textile_id - Textile Id
 * @prop {string=} textile_item_id - Textile Item Id
 * @prop {string|null=} comment - Comment
 * @prop {Number=} selection_status - Selection Status (0, 1, 3)
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editMoodboardItemFormData = {
	moodboard_id?: string;
	textile_id?: string;
	textile_item_id?: string;
	comment?: string|null;
	selection_status?: Number;
	status?: Number;
};

/**
 * @typedef {Object} listMoodboardUsersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listMoodboardUsersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addMoodboardUserFormData Moodboard User's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string} moodboard_id - Moodboard Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string} user_email - User Email
 * @prop {string|null=} user_name - User Name
 * @prop {Number} public_access - Public Access
 * @prop {Number} private_access - Private Access
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {boolean|null=} revoked - Revoked
 * @prop {string|null=} revoker - Revoker
 */
export type addMoodboardUserFormData = {
	platform_id?: string|null;
	moodboard_id: string;
	client_organization_id?: string|null;
	user_id?: string|null;
	contact_id?: string|null;
	user_email: string;
	user_name?: string|null;
	public_access: Number;
	private_access: Number;
	status: Number;
	revoked?: boolean|null;
	revoker?: string|null;
};

/**
 * @typedef {Object} editMoodboardUserFormData Moodboard User's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string=} moodboard_id - Moodboard Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string=} user_email - User Email
 * @prop {string|null=} user_name - User Name
 * @prop {Number=} public_access - Public Access
 * @prop {Number=} private_access - Private Access
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {boolean|null=} revoked - Revoked
 * @prop {string|null=} revoker - Revoker
 */
export type editMoodboardUserFormData = {
	platform_id?: string|null;
	moodboard_id?: string;
	client_organization_id?: string|null;
	user_id?: string|null;
	contact_id?: string|null;
	user_email?: string;
	user_name?: string|null;
	public_access?: Number;
	private_access?: Number;
	status?: Number;
	revoked?: boolean|null;
	revoker?: string|null;
};

/**
 * @typedef {Object} listMoodboardsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listMoodboardsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewAllByOrganizationIdMoodboardFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewAllByOrganizationIdMoodboardFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addMoodboardFormData Moodboard's data
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string} name - Name
 * @prop {string|null=} description - Description
 * @prop {string|null=} slug - Slug
 * @prop {Number} status - Status (0, 1, 2, 99)
 */
export type addMoodboardFormData = {
	organization_id?: string|null;
	user_id?: string|null;
	name: string;
	description?: string|null;
	slug?: string|null;
	status: Number;
};

/**
 * @typedef {Object} editMoodboardFormData Moodboard's data
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string=} name - Name
 * @prop {string|null=} description - Description
 * @prop {string|null=} slug - Slug
 * @prop {Number=} status - Status (0, 1, 2, 99)
 */
export type editMoodboardFormData = {
	organization_id?: string|null;
	user_id?: string|null;
	name?: string;
	description?: string|null;
	slug?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} listNewItemColorSpaceDefaultsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listNewItemColorSpaceDefaultsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addNewItemColorSpaceDefaultFormData New Item Color Space Default's data
 * @prop {string} new_textile_item_default_id - New Textile Item Default Id
 * @prop {string} color_space_id - Color Space Id
 * @prop {Number} status - Status (0, 1, 50, 99)
 */
export type addNewItemColorSpaceDefaultFormData = {
	new_textile_item_default_id: string;
	color_space_id: string;
	status: Number;
};

/**
 * @typedef {Object} editNewItemColorSpaceDefaultFormData New Item Color Space Default's data
 * @prop {string=} new_textile_item_default_id - New Textile Item Default Id
 * @prop {string=} color_space_id - Color Space Id
 * @prop {Number=} status - Status (0, 1, 50, 99)
 */
export type editNewItemColorSpaceDefaultFormData = {
	new_textile_item_default_id?: string;
	color_space_id?: string;
	status?: Number;
};

/**
 * @typedef {Object} listNewTextileItemColorSpacesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listNewTextileItemColorSpacesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addNewTextileItemColorSpaceFormData New Textile Item Color Space's data
 * @prop {string} new_textile_item_id - New Textile Item Id
 * @prop {string} color_space_id - Color Space Id
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addNewTextileItemColorSpaceFormData = {
	new_textile_item_id: string;
	color_space_id: string;
	status: Number;
};

/**
 * @typedef {Object} editNewTextileItemColorSpaceFormData New Textile Item Color Space's data
 * @prop {string=} new_textile_item_id - New Textile Item Id
 * @prop {string=} color_space_id - Color Space Id
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editNewTextileItemColorSpaceFormData = {
	new_textile_item_id?: string;
	color_space_id?: string;
	status?: Number;
};

/**
 * @typedef {Object} listNewTextileItemDefaultsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listNewTextileItemDefaultsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addNewTextileItemDefaultFormData New Textile Item Default's data
 * @prop {string} textile_production_order_default_id - Textile Production Order Default Id
 * @prop {string} new_item_min_order_quantity_unit - New Item Min Order Quantity Unit
 * @prop {Number} new_item_min_order_quantity_unit_amount - New Item Min Order Quantity Unit Amount
 * @prop {string} new_item_avg_lead_time_unit - New Item Avg Lead Time Unit
 * @prop {Number} new_item_avg_lead_time_unit_amount - New Item Avg Lead Time Unit Amount
 * @prop {Number} status - Status (0, 1, 50, 99)
 */
export type addNewTextileItemDefaultFormData = {
	textile_production_order_default_id: string;
	new_item_min_order_quantity_unit: string;
	new_item_min_order_quantity_unit_amount: Number;
	new_item_avg_lead_time_unit: string;
	new_item_avg_lead_time_unit_amount: Number;
	status: Number;
};

/**
 * @typedef {Object} editNewTextileItemDefaultFormData New Textile Item Default's data
 * @prop {string=} textile_production_order_default_id - Textile Production Order Default Id
 * @prop {string=} new_item_min_order_quantity_unit - New Item Min Order Quantity Unit
 * @prop {Number=} new_item_min_order_quantity_unit_amount - New Item Min Order Quantity Unit Amount
 * @prop {string=} new_item_avg_lead_time_unit - New Item Avg Lead Time Unit
 * @prop {Number=} new_item_avg_lead_time_unit_amount - New Item Avg Lead Time Unit Amount
 * @prop {Number=} status - Status (0, 1, 50, 99)
 * @prop {boolean=} propagation - if set to true, will also edit new_textile_items data that are set to use default data.
 */
export type editNewTextileItemDefaultFormData = {
	textile_production_order_default_id?: string;
	new_item_min_order_quantity_unit?: string;
	new_item_min_order_quantity_unit_amount?: Number;
	new_item_avg_lead_time_unit?: string;
	new_item_avg_lead_time_unit_amount?: Number;
	status?: Number;
	propagation?: boolean;
};

/**
 * @typedef {Object} listNewTextileItemsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listNewTextileItemsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addNewTextileItemFormData New Textile Item's data
 * @prop {string} textile_production_order_requirement_id - Textile Production Order Requirement Id
 * @prop {string} new_item_min_order_quantity_unit - New Item Min Order Quantity Unit
 * @prop {Number} new_item_min_order_quantity_unit_amount - New Item Min Order Quantity Unit Amount
 * @prop {string} new_item_avg_lead_time_unit - New Item Avg Lead Time Unit
 * @prop {Number} new_item_avg_lead_time_unit_amount - New Item Avg Lead Time Unit Amount
 * @prop {boolean} use_default - Use Default
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addNewTextileItemFormData = {
	textile_production_order_requirement_id: string;
	new_item_min_order_quantity_unit: string;
	new_item_min_order_quantity_unit_amount: Number;
	new_item_avg_lead_time_unit: string;
	new_item_avg_lead_time_unit_amount: Number;
	use_default: boolean;
	status: Number;
};

/**
 * @typedef {Object} editNewTextileItemFormData New Textile Item's data
 * @prop {string=} textile_production_order_requirement_id - Textile Production Order Requirement Id
 * @prop {string=} new_item_min_order_quantity_unit - New Item Min Order Quantity Unit
 * @prop {Number=} new_item_min_order_quantity_unit_amount - New Item Min Order Quantity Unit Amount
 * @prop {string=} new_item_avg_lead_time_unit - New Item Avg Lead Time Unit
 * @prop {Number=} new_item_avg_lead_time_unit_amount - New Item Avg Lead Time Unit Amount
 * @prop {boolean=} use_default - Use Default
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editNewTextileItemFormData = {
	textile_production_order_requirement_id?: string;
	new_item_min_order_quantity_unit?: string;
	new_item_min_order_quantity_unit_amount?: Number;
	new_item_avg_lead_time_unit?: string;
	new_item_avg_lead_time_unit_amount?: Number;
	use_default?: boolean;
	status?: Number;
};

/**
 * @typedef {Object} addOrganizationCatalogProductFormData Organization catalog product data
 * @prop {string} organization_catalog_id - Organization catalog's id that needs to be linked
 * @prop {string} product_type - Product's type
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 */
export type addOrganizationCatalogProductFormData = {
	organization_catalog_id: string;
	product_type: string;
	textile_id: string;
	textile_item_id: string;
};

/**
 * @typedef {Object} editOrganizationCatalogProductFormData Organization catalog product data
 * @prop {string=} organization_catalog_id - Organization catalog's id that needs to be linked
 * @prop {string=} product_type - Product's type
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 */
export type editOrganizationCatalogProductFormData = {
	organization_catalog_id?: string;
	product_type?: string;
	textile_id?: string;
	textile_item_id?: string;
};

/**
 * @typedef {Object} addOrganizationsCatalogFormData Organization catalog data
 * @prop {string} catalog_id - Mechanical's id that needs to be linked
 */
export type addOrganizationsCatalogFormData = {
	catalog_id: string;
};

/**
 * @typedef {Object} editOrganizationsCatalogFormData Collection product data
 * @prop {string=} catalog_id - Mechanical's id that needs to be linked
 */
export type editOrganizationsCatalogFormData = {
	catalog_id?: string;
};

/**
 * @typedef {Object} addOriginFormData Origin data
 * @prop {string} layer_id - Layer's id that needs to be linked
 * @prop {string} country_origin - Which country the layer originates from
 * @prop {string} element - One of these 3 options: Material/Yarn/Fibers
 * @prop {string} file_id - Id of the file found in FM that will represent the origin
 * @prop {Number=} visibility - Whether or not the origin is visible defaults to false (0)
 */
export type addOriginFormData = {
	layer_id: string;
	country_origin: string;
	element: string;
	file_id: string;
	visibility?: Number;
};

/**
 * @typedef {Object} editOriginFormData Origin data
 * @prop {string=} layer_id - Layer's id that needs to be linked
 * @prop {string=} country_origin - Which country the layer originates from
 * @prop {string=} element - One of these 3 options: Material/Yarn/Fibers
 * @prop {string=} file_id - Id of the file found in FM that will represent the origin
 * @prop {Number=} visibility - Whether or not the origin is visible
 */
export type editOriginFormData = {
	layer_id?: string;
	country_origin?: string;
	element?: string;
	file_id?: string;
	visibility?: Number;
};

/**
 * @typedef {Object} listParticipantMerchantsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listParticipantMerchantsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addParticipantMerchantFormData Participant Merchant's data
 * @prop {string} platform_catalog_id - Platform Catalog Id
 * @prop {string} organization_id - Organization Id
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {string} platform_note - Platform Note
 */
export type addParticipantMerchantFormData = {
	platform_catalog_id: string;
	organization_id: string;
	status: Number;
	platform_note: string;
};

/**
 * @typedef {Object} editParticipantMerchantFormData Participant Merchant's data
 * @prop {string=} platform_catalog_id - Platform Catalog Id
 * @prop {string=} organization_id - Organization Id
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {string=} platform_note - Platform Note
 */
export type editParticipantMerchantFormData = {
	platform_catalog_id?: string;
	organization_id?: string;
	status?: Number;
	platform_note?: string;
};

/**
 * @typedef {Object} listPerformancesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPerformancesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPerformanceFormData Performance's data
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addPerformanceFormData = {
	text: string;
	translation_slug?: string|null;
	status: Number;
};

/**
 * @typedef {Object} editPerformanceFormData Performance's data
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editPerformanceFormData = {
	text?: string;
	translation_slug?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} addPerformancesTextileFormData Performance textile data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} performance_id - Performance's id that needs to be linked
 * @prop {string=} file_id - Id of the file found in FM that will represent the performance
 * @prop {boolean|null=} visibility - Whether or not the record is visible defaults to false (0)
 */
export type addPerformancesTextileFormData = {
	textile_id: string;
	performance_id: string;
	file_id?: string;
	visibility?: boolean|null;
};

/**
 * @typedef {Object} editPerformancesTextileFormData Performance textile data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} performance_id - Performance's id that needs to be linked
 * @prop {string=} file_id - Id of the file found in FM that will represent the performance
 * @prop {boolean|null=} visibility - Whether or not the record is visible defaults to false (0)
 */
export type editPerformancesTextileFormData = {
	textile_id?: string;
	performance_id?: string;
	file_id?: string;
	visibility?: boolean|null;
};

/**
 * @typedef {Object} listPlatformCatalogProductsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listPlatformCatalogProductsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewCatalogProductsByPlatformPlatformCatalogProductFormData
 * @prop {string} organization_id
 * @prop {string} platform_model
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewCatalogProductsByPlatformPlatformCatalogProductFormData = {
	organization_id: string;
	platform_model: string;
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewShowroomProductByOrganizationIdPlatformCatalogProductFormData
 * @prop {string} organization_id
 * @prop {string} platform_model
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewShowroomProductByOrganizationIdPlatformCatalogProductFormData = {
	organization_id: string;
	platform_model: string;
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addPlatformCatalogProductFormData Platform Catalog Product's data
 * @prop {string} platform_catalog_id - Platform Catalog Id
 * @prop {Number|null=} platform_requested - Platform Requested
 * @prop {string} product_type - Product Type
 * @prop {string|null=} textile_id - Textile Id
 * @prop {string|null=} textile_item_id - Textile Item Id
 * @prop {string|null=} fiber_id - Fiber Id
 * @prop {string|null=} fiber_item_id - Fiber Item Id
 * @prop {Number} request_status - Request Status
 */
export type addPlatformCatalogProductFormData = {
	platform_catalog_id: string;
	platform_requested?: Number|null;
	product_type: string;
	textile_id?: string|null;
	textile_item_id?: string|null;
	fiber_id?: string|null;
	fiber_item_id?: string|null;
	request_status: Number;
};

/**
 * @typedef {Object} editPlatformCatalogProductFormData Platform Catalog Product's data
 * @prop {string=} platform_catalog_id - Platform Catalog Id
 * @prop {Number|null=} platform_requested - Platform Requested
 * @prop {string=} product_type - Product Type
 * @prop {string|null=} textile_id - Textile Id
 * @prop {string|null=} textile_item_id - Textile Item Id
 * @prop {string|null=} fiber_id - Fiber Id
 * @prop {string|null=} fiber_item_id - Fiber Item Id
 * @prop {Number=} request_status - Request Status
 */
export type editPlatformCatalogProductFormData = {
	platform_catalog_id?: string;
	platform_requested?: Number|null;
	product_type?: string;
	textile_id?: string|null;
	textile_item_id?: string|null;
	fiber_id?: string|null;
	fiber_item_id?: string|null;
	request_status?: Number;
};

/**
 * @typedef {Object} addPlatformsCatalogFormData Platform catalog data
 * @prop {string} platform_model - The platform's type of model. Valid values are: 'System', 'Showroom', 'Marketplaces', 'Corporate', 'Brochure'
 * @prop {string} platform_id - Platform's id that needs to be linked
 * @prop {string} catalog_id - Catalog's id that needs to be linked
 */
export type addPlatformsCatalogFormData = {
	platform_model: string;
	platform_id: string;
	catalog_id: string;
};

/**
 * @typedef {Object} viewPlatformProductsPlatformsCatalogFormData
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewPlatformProductsPlatformsCatalogFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addDisplayRequestPlatformsCatalogFormData Platform Catalog Product's data
 * @prop {string} platform_id - Platform Catalog Id
 * @prop {Number|null=} platform_requested - Platform Requested
 * @prop {string} product_type - Product Type
 * @prop {string|null=} textile_id - Textile Id
 * @prop {string|null=} textile_item_id - Textile Item Id
 * @prop {string|null=} fiber_id - Fiber Id
 * @prop {string|null=} fiber_item_id - Fiber Item Id
 * @prop {Number} request_status - Request Status
 */
export type addDisplayRequestPlatformsCatalogFormData = {
	platform_id: string;
	platform_requested?: Number|null;
	product_type: string;
	textile_id?: string|null;
	textile_item_id?: string|null;
	fiber_id?: string|null;
	fiber_item_id?: string|null;
	request_status: Number;
};

/**
 * @typedef {Object} editPlatformsCatalogFormData Platform catalog data
 * @prop {string=} platform_model - The platform's type of model. Valid values are: 'System', 'Showroom', 'Marketplaces', 'Corporate', 'Brochure'
 * @prop {string=} platform_id - Platform's id that needs to be linked
 * @prop {string=} catalog_id - Catalog's id that needs to be linked
 */
export type editPlatformsCatalogFormData = {
	platform_model?: string;
	platform_id?: string;
	catalog_id?: string;
};

/**
 * @typedef {Object} viewMerchantDisplayRequestsPlatformsCatalogFormData Optional request data
 * @prop {string|null=} organization_id - Textile's organization ID
 * @prop {string|null=} tabView - Page tab view
 */
export type viewMerchantDisplayRequestsPlatformsCatalogFormData = {
	organization_id?: string|null;
	tabView?: string|null;
};

/**
 * @typedef {Object} viewProductsByPlatformIdOrganizationsIdsPlatformsCatalogFormData Request data
 * @prop {{}} organization_ids - Textile's organization IDs
 * @prop {boolean|null=} count_only - Return only counted products foreach organization_id
 */
export type viewProductsByPlatformIdOrganizationsIdsPlatformsCatalogFormData = {
	organization_ids: {};
	count_only?: boolean|null;
};

/**
 * @typedef {Object} addProcurementsCatalogFormData Procurement catalog data
 * @prop {string} organization_id - Organization's id that needs to be linked
 * @prop {string} service_id - Service's id that needs to be linked
 * @prop {string} catalog_id - Catalog's id that needs to be linked
 */
export type addProcurementsCatalogFormData = {
	organization_id: string;
	service_id: string;
	catalog_id: string;
};

/**
 * @typedef {Object} editProcurementsCatalogFormData Procurement catalog data
 * @prop {string=} organization_id - Organization's id that needs to be linked
 * @prop {string=} service_id - Service's id that needs to be linked
 * @prop {string=} catalog_id - Catalog's id that needs to be linked
 */
export type editProcurementsCatalogFormData = {
	organization_id?: string;
	service_id?: string;
	catalog_id?: string;
};

/**
 * @typedef {Object} addProductFormData Product data
 * @prop {Number=} tengiva_product_code - Unique number that identifies a product within Tengiva
 * @prop {string} organization_id - Organization's id that needs to be linked
 * @prop {string} type - Product's type (Textiles/Yarns/Fibers/Trims)
 * @prop {Number=} status - Product's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 0)
 */
export type addProductFormData = {
	tengiva_product_code?: Number;
	organization_id: string;
	type: string;
	status?: Number;
};

/**
 * @typedef {Object} editProductFormData Product data
 * @prop {Number=} tengiva_product_code - Unique number that identifies a product within Tengiva
 * @prop {string=} organization_id - Organization's id that needs to be linked
 * @prop {string=} type - Product's type (Textiles/Yarns/Fibers/Trims)
 * @prop {Number=} status - Product's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 0)
 */
export type editProductFormData = {
	tengiva_product_code?: Number;
	organization_id?: string;
	type?: string;
	status?: Number;
};

/**
 * @typedef {Object} createTextileFromQueueProductFormData
 * @prop {string=} product_name - Textile's name as given by the supplier. It corresponds to 'textiles' table's 'supplier_product' column.
 * @prop {{}=} color_identification - Textile item's name of color as given by the supplier. It corresponds to 'textiles_items' table's 'organization_textile_item_code' column.
 * @prop {string} organization_id - Id of the organization this textile belongs to.
 * @prop {string=} textile_id - If only textile items are created, textile_id becomes required to link the items properly to their corresponding textile.
 */
export type createTextileFromQueueProductFormData = {
	product_name?: string;
	color_identification?: {};
	organization_id: string;
	textile_id?: string;
};

/**
 * @typedef {Object} addQualityControlFormData Quality control data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} file_id - Id of the file found in FM that will represent the quality control
 * @prop {string} name - Quality control's name
 * @prop {boolean} visibility - Quality control's visibility
 */
export type addQualityControlFormData = {
	textile_id: string;
	file_id: string;
	name: string;
	visibility: boolean;
};

/**
 * @typedef {Object} editQualityControlFormData Quality control data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} file_id - Id of the file found in FM that will represent the quality control
 * @prop {string=} name - Quality control's name
 * @prop {boolean=} visibility - Quality control's visibility
 */
export type editQualityControlFormData = {
	textile_id?: string;
	file_id?: string;
	name?: string;
	visibility?: boolean;
};

/**
 * @typedef {Object} addRawMaterialsCatalogFormData Raw material catalog data
 * @prop {string} organization_id - Organization's id that needs to be linked
 * @prop {string} catalog_id - Catalog's id that needs to be linked
 */
export type addRawMaterialsCatalogFormData = {
	organization_id: string;
	catalog_id: string;
};

/**
 * @typedef {Object} editRawMaterialsCatalogFormData Raw material catalog data
 * @prop {string=} organization_id - Organization's id that needs to be linked
 * @prop {string=} catalog_id - Catalog's id that needs to be linked
 */
export type editRawMaterialsCatalogFormData = {
	organization_id?: string;
	catalog_id?: string;
};

/**
 * @typedef {Object} listReasonOfAvailabilitiesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listReasonOfAvailabilitiesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addReasonOfAvailabilityFormData Reason Of Availability's data
 * @prop {string} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addReasonOfAvailabilityFormData = {
	text: string;
	translation_slug?: string|null;
	status: Number;
};

/**
 * @typedef {Object} editReasonOfAvailabilityFormData Reason Of Availability's data
 * @prop {string=} text - Text
 * @prop {string|null=} translation_slug - Translation Slug
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editReasonOfAvailabilityFormData = {
	text?: string;
	translation_slug?: string|null;
	status?: Number;
};

/**
 * @typedef {Object} listSharedAccessUsersFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listSharedAccessUsersFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addSharedAccessUserFormData Shared Access User's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string} textile_id - Textile Id
 * @prop {string} textile_item_id - Textile Item Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string} user_email - User Email
 * @prop {string|null=} user_name - User Name
 * @prop {boolean} private_access - Private Access
 * @prop {Number} status - Status (0, 1, 99)
 * @prop {string|null=} revoker - Revoker
 */
export type addSharedAccessUserFormData = {
	platform_id?: string|null;
	textile_id: string;
	textile_item_id: string;
	client_organization_id?: string|null;
	user_id?: string|null;
	contact_id?: string|null;
	user_email: string;
	user_name?: string|null;
	private_access: boolean;
	status: Number;
	revoker?: string|null;
};

/**
 * @typedef {Object} editSharedAccessUserFormData Shared Access User's data
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string=} textile_id - Textile Id
 * @prop {string=} textile_item_id - Textile Item Id
 * @prop {string|null=} client_organization_id - Client Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string=} user_email - User Email
 * @prop {string|null=} user_name - User Name
 * @prop {boolean=} private_access - Private Access
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {string|null=} revoker - Revoker
 */
export type editSharedAccessUserFormData = {
	platform_id?: string|null;
	textile_id?: string;
	textile_item_id?: string;
	client_organization_id?: string|null;
	user_id?: string|null;
	contact_id?: string|null;
	user_email?: string;
	user_name?: string|null;
	private_access?: boolean;
	status?: Number;
	revoker?: string|null;
};

/**
 * @typedef {Object} addManySharedAccessUserFormData
 * @prop {{}} users - list of emails to process
 * @prop {string} platform_id - platform_id the textile item belongs to
 * @prop {boolean} private_access - whether or not the users will have access to the product when it is private
 * @prop {string} item_image - url that will display the product's image, corresponds to FM table's pm_files {full_path}{id}
 */
export type addManySharedAccessUserFormData = {
	users: {};
	platform_id: string;
	private_access: boolean;
	item_image: string;
};

/**
 * @typedef {Object} viewProductsShowroomFormData
 * @prop {{}=} filters - List of filters to apply on the results
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewProductsShowroomFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewCollectionShowroomFormData
 * @prop {{}=} filters - List of filters to apply on the results
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewCollectionShowroomFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addOrganizationDefaultsForSupplyChainSubscriptionFormData
 * @prop {string} organization_id - Id of the organization to which the defaults will be created for.
 * @prop {string|null=} platform_id - Id of the platform to which the defaults will be created for.
 */
export type addOrganizationDefaultsForSupplyChainSubscriptionFormData = {
	organization_id: string;
	platform_id?: string|null;
};

/**
 * @typedef {Object} addOrganizationDefaultsForMarketplaceSubscriptionFormData
 * @prop {string} organization_id - Id of the organization to which the defaults will be created for.
 * @prop {string} platform_id - Id of the platform to which the defaults will be created for.
 */
export type addOrganizationDefaultsForMarketplaceSubscriptionFormData = {
	organization_id: string;
	platform_id: string;
};

/**
 * @typedef {Object} addTextileItemsMetaFormData Textile item meta data
 * @prop {string} textile_meta_id - Textile meta's id that needs to be linked
 * @prop {string} digitization_workflow_id - Digitization workflow's id that needs to be linked
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {Number} current_version_number - Textile item meta's version number. Used for versioning
 * @prop {string} qr_id - Qr code's id
 * @prop {string} keywords - Keywords intended for SEO
 * @prop {string=} description - Textile item meta's description
 * @prop {string} img_id - Image's id
 */
export type addTextileItemsMetaFormData = {
	textile_meta_id: string;
	digitization_workflow_id: string;
	textile_item_id: string;
	current_version_number: Number;
	qr_id: string;
	keywords: string;
	description?: string;
	img_id: string;
};

/**
 * @typedef {Object} editTextileItemsMetaFormData Textile item meta data
 * @prop {string=} textile_meta_id - Textile meta's id that needs to be linked
 * @prop {string=} digitization_workflow_id - Digitization workflow's id that needs to be linked
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {Number=} current_version_number - Textile item meta's version number. Used for versioning
 * @prop {string=} qr_id - Qr code's id
 * @prop {string=} keywords - Keywords intended for SEO
 * @prop {string=} description - Textile item meta's description
 * @prop {string=} img_id - Image's id
 */
export type editTextileItemsMetaFormData = {
	textile_meta_id?: string;
	digitization_workflow_id?: string;
	textile_item_id?: string;
	current_version_number?: Number;
	qr_id?: string;
	keywords?: string;
	description?: string;
	img_id?: string;
};

/**
 * @typedef {Object} listTextileProductionOrderRequirementDefaultsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listTextileProductionOrderRequirementDefaultsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addTextileProductionOrderRequirementDefaultFormData Textile Production Order Requirement Default's data
 * @prop {string} organization_id - Organization Id
 * @prop {string} textile_min_order_quantity_unit - Textile Min Order Quantity Unit
 * @prop {Number} textile_min_order_quantity_unit_amount - Textile Min Order Quantity Unit Amount
 * @prop {Number} status - Status (0, 1, 50, 99)
 */
export type addTextileProductionOrderRequirementDefaultFormData = {
	organization_id: string;
	textile_min_order_quantity_unit: string;
	textile_min_order_quantity_unit_amount: Number;
	status: Number;
};

/**
 * @typedef {Object} editTextileProductionOrderRequirementDefaultFormData Textile Production Order Requirement Default's data
 * @prop {string=} organization_id - Organization Id
 * @prop {string=} textile_min_order_quantity_unit - Textile Min Order Quantity Unit
 * @prop {Number=} textile_min_order_quantity_unit_amount - Textile Min Order Quantity Unit Amount
 * @prop {Number=} status - Status (0, 1, 50, 99)
 * @prop {{}=} existing_textile_item_default - Associated existing textile item default data
 * @prop {{}=} new_textile_item_default - Associated new textile item default data
 * @prop {boolean=} propagation - if set to true, will also edit textile_production_order_requirements data that are set to use default data.
 */
export type editTextileProductionOrderRequirementDefaultFormData = {
	organization_id?: string;
	textile_min_order_quantity_unit?: string;
	textile_min_order_quantity_unit_amount?: Number;
	status?: Number;
	existing_textile_item_default?: {};
	new_textile_item_default?: {};
	propagation?: boolean;
};

/**
 * @typedef {Object} listTextileProductionOrderRequirementsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listTextileProductionOrderRequirementsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewForTextileIdsTextileProductionOrderRequirementFormData
 * @prop {{}} textiles
 */
export type viewForTextileIdsTextileProductionOrderRequirementFormData = {
	textiles: {};
};

/**
 * @typedef {Object} addTextileProductionOrderRequirementFormData Textile Production Order Requirement's data
 * @prop {string} textile_production_order_default_id - Textile Production Order Default Id
 * @prop {string} textile_id - Textile Id
 * @prop {string} textile_min_order_quantity_unit - Textile Min Order Quantity Unit
 * @prop {Number} textile_min_order_quantity_unit_amount - Textile Min Order Quantity Unit Amount
 * @prop {boolean} use_default - Use Default
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addTextileProductionOrderRequirementFormData = {
	textile_production_order_default_id: string;
	textile_id: string;
	textile_min_order_quantity_unit: string;
	textile_min_order_quantity_unit_amount: Number;
	use_default: boolean;
	status: Number;
};

/**
 * @typedef {Object} editTextileProductionOrderRequirementFormData Textile Production Order Requirement's data
 * @prop {string=} textile_production_order_default_id - Textile Production Order Default Id
 * @prop {string=} textile_id - Textile Id
 * @prop {string=} textile_min_order_quantity_unit - Textile Min Order Quantity Unit
 * @prop {Number=} textile_min_order_quantity_unit_amount - Textile Min Order Quantity Unit Amount
 * @prop {boolean=} use_default - Use Default
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editTextileProductionOrderRequirementFormData = {
	textile_production_order_default_id?: string;
	textile_id?: string;
	textile_min_order_quantity_unit?: string;
	textile_min_order_quantity_unit_amount?: Number;
	use_default?: boolean;
	status?: Number;
};

/**
 * @typedef {Object} listTextilesApplicationsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listTextilesApplicationsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} listCategoriesByPlatformTextilesApplicationFormData
 * @prop {string} organization_id
 * @prop {string} platform_model
 */
export type listCategoriesByPlatformTextilesApplicationFormData = {
	organization_id: string;
	platform_model: string;
};

/**
 * @typedef {Object} addTextilesApplicationFormData Textiles Application's data
 * @prop {string} textile_id - Textile Id
 * @prop {string|null=} application_id - Application Id
 * @prop {Number} sequence - Sequence
 */
export type addTextilesApplicationFormData = {
	textile_id: string;
	application_id?: string|null;
	sequence: Number;
};

/**
 * @typedef {Object} editTextilesApplicationFormData Textiles Application's data
 * @prop {string=} textile_id - Textile Id
 * @prop {string|null=} application_id - Application Id
 * @prop {Number=} sequence - Sequence
 */
export type editTextilesApplicationFormData = {
	textile_id?: string;
	application_id?: string|null;
	sequence?: Number;
};

/**
 * @typedef {Object} addTextileFormData Textile data
 * @prop {string} organization_id - Organization's id that needs to be linked
 * @prop {string} product_id - Product's id that needs to be linked
 * @prop {Number=} tengiva_textile_code - Unique number that identifies a textile within Tengiva
 * @prop {string=} organization_textile_name - Supplier's name for the textile
 * @prop {string} organization_textile_code - Supplier's code for the textile
 * @prop {string=} country_origin - Textile's country of origin
 * @prop {string=} name - Textile's name
 * @prop {string=} compiled_short_description - Textile's compiled short description
 * @prop {string=} custom_short_description - Textile's custom short description
 * @prop {string=} fluidity - Textile's fluidity
 * @prop {string=} extensibility - Textile's extensibility
 * @prop {string=} transparency - Textile's transparency
 * @prop {string=} thickness - Textile's thickness described as an adjective (ex: Thin, Thick)
 * @prop {Number=} thickness_amount - Textile's thickness described as a number
 * @prop {Number=} textile_weight - Textile's weight
 * @prop {string=} textile_weight_unit - Textile's weight unit
 * @prop {Number=} open_width - Textile's open width
 * @prop {string=} open_width_unit - Textile's open width unit
 * @prop {boolean=} circular - Wether or not the textile has a circular shape
 * @prop {string=} product_side_note - Note related to the textile
 * @prop {string=} washing - Textile's preferred method of washing
 * @prop {string=} drying - Textile's preferred method of drying
 * @prop {string=} washing_temperature - Textile's preferred temperature of washing
 * @prop {string=} dry_clean - Is the textile dry cleanable or not? Is dry cleaning the only cleaning method for thix textile?
 * @prop {boolean=} only_businesses - Is the textile only visible to businesses
 * @prop {Number=} status - Textile's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 0)
 * @prop {boolean=} visibility - Textile item's visibility status (0 Or 1, 0:Private, 1:Public, Defaults to 0)
 * @prop {string|null=} activation_date - When was this textile switched to be active?
 * @prop {{}=} textiles_metas - Associated textile meta data
 * @prop {{}=} textiles_other_names - Associated textile other name data
 * @prop {{}=} performances_textiles - Associated performance textile data
 * @prop {{}=} mechanicals_textiles - Associated mechanical data
 * @prop {{}=} chemicals_textiles - Associated chemical data
 * @prop {{}=} certifications_textiles - Associated certification textile data
 * @prop {{}=} quality_controls - Associated quality control data
 * @prop {{}=} textiles_applications - Associated textile application data
 * @prop {{}=} layers - Associated layer data
 * @prop {{}=} textiles_items - Associated textile item data
 */
export type addTextileFormData = {
	organization_id: string;
	product_id: string;
	tengiva_textile_code?: Number;
	organization_textile_name?: string;
	organization_textile_code: string;
	country_origin?: string;
	name?: string;
	compiled_short_description?: string;
	custom_short_description?: string;
	fluidity?: string;
	extensibility?: string;
	transparency?: string;
	thickness?: string;
	thickness_amount?: Number;
	textile_weight?: Number;
	textile_weight_unit?: string;
	open_width?: Number;
	open_width_unit?: string;
	circular?: boolean;
	product_side_note?: string;
	washing?: string;
	drying?: string;
	washing_temperature?: string;
	dry_clean?: string;
	only_businesses?: boolean;
	status?: Number;
	visibility?: boolean;
	activation_date?: string|null;
	textiles_metas?: {};
	textiles_other_names?: {};
	performances_textiles?: {};
	mechanicals_textiles?: {};
	chemicals_textiles?: {};
	certifications_textiles?: {};
	quality_controls?: {};
	textiles_applications?: {};
	layers?: {};
	textiles_items?: {};
};

/**
 * @typedef {Object} viewAllByOrganizationIdTextileFormData
 * @prop {Number=} textile_item_status - Status number of textile item
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type viewAllByOrganizationIdTextileFormData = {
	textile_item_status?: Number;
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} regenerateOrganizationDefaultCatalogsTextileFormData Organization catalog data
 * @prop {string} platform_id - Platform id of the platform to regenerate list of textile for organization
 * @prop {boolean} purchased_package - Platform id of the platform to regenerate list of textile for organization
 */
export type regenerateOrganizationDefaultCatalogsTextileFormData = {
	platform_id: string;
	purchased_package: boolean;
};

/**
 * @typedef {Object} editTextileFormData Textile data
 * @prop {string=} organization_id - Organization's id that needs to be linked
 * @prop {string=} product_id - Product's id that needs to be linked
 * @prop {Number=} tengiva_textile_code - Unique number that identifies a textile within Tengiva
 * @prop {string=} organization_textile_name - Supplier's name for the textile
 * @prop {string} organization_textile_code - Supplier's code for the textile
 * @prop {string=} country_origin - Textile's country of origin
 * @prop {string=} name - Textile's name
 * @prop {string=} compiled_short_description - Textile's compiled short description
 * @prop {string=} custom_short_description - Textile's custom short description
 * @prop {string=} fluidity - Textile's fluidity
 * @prop {string=} extensibility - Textile's extensibility
 * @prop {string=} transparency - Textile's transparency
 * @prop {string=} thickness - Textile's thickness described as an adjective (ex: Thin, Thick)
 * @prop {Number=} thickness_amount - Textile's thickness described as a number
 * @prop {Number=} textile_weight - Textile's weight
 * @prop {string=} textile_weight_unit - Textile's weight unit
 * @prop {Number=} open_width - Textile's open width
 * @prop {string=} open_width_unit - Textile's open width unit
 * @prop {boolean=} circular - Wether or not the textile has a circular shape
 * @prop {string=} product_side_note - Note related to the textile
 * @prop {string=} washing - Textile's preferred method of washing
 * @prop {string=} drying - Textile's preferred method of drying
 * @prop {string=} washing_temperature - Textile's preferred temperature of washing
 * @prop {string=} dry_clean - Is the textile dry cleanable or not? Is dry cleaning the only cleaning method for thix textile?
 * @prop {boolean=} only_businesses - Is the textile only visible to businesses
 * @prop {Number=} status - Textile's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 0)
 * @prop {boolean=} visibility - Textile item's visibility status (0 Or 1, 0:Private, 1:Public, Defaults to 0)
 * @prop {string|null=} activation_date - When was this textile switched to be active?
 * @prop {{}=} textiles_metas - Associated textile meta data
 * @prop {{}=} textiles_other_names - Associated textile other name data
 * @prop {{}=} performances_textiles - Associated performance textile data
 * @prop {{}=} mechanicals_textiles - Associated mechanical data
 * @prop {{}=} chemicals_textiles - Associated chemical data
 * @prop {{}=} certifications_textiles - Associated certification textile data
 * @prop {{}=} quality_controls - Associated quality control data
 * @prop {{}=} textiles_applications - Associated textile application data
 * @prop {{}=} layers - Associated layer data
 * @prop {{}=} textiles_items - Associated textile item data
 */
export type editTextileFormData = {
	organization_id?: string;
	product_id?: string;
	tengiva_textile_code?: Number;
	organization_textile_name?: string;
	organization_textile_code: string;
	country_origin?: string;
	name?: string;
	compiled_short_description?: string;
	custom_short_description?: string;
	fluidity?: string;
	extensibility?: string;
	transparency?: string;
	thickness?: string;
	thickness_amount?: Number;
	textile_weight?: Number;
	textile_weight_unit?: string;
	open_width?: Number;
	open_width_unit?: string;
	circular?: boolean;
	product_side_note?: string;
	washing?: string;
	drying?: string;
	washing_temperature?: string;
	dry_clean?: string;
	only_businesses?: boolean;
	status?: Number;
	visibility?: boolean;
	activation_date?: string|null;
	textiles_metas?: {};
	textiles_other_names?: {};
	performances_textiles?: {};
	mechanicals_textiles?: {};
	chemicals_textiles?: {};
	certifications_textiles?: {};
	quality_controls?: {};
	textiles_applications?: {};
	layers?: {};
	textiles_items?: {};
};

/**
 * @typedef {Object} getDimensionsTextileFormData
 * @prop {{}} textile_item_ids - Array that contains every textile_item_id whose associated textile's dimensions are needed.
 */
export type getDimensionsTextileFormData = {
	textile_item_ids: {};
};

/**
 * @typedef {Object} getTextileAndItemNameByIdTextileFormData
 * @prop {{}} textiles - Array that contains every textile_id whose names are needed.
 */
export type getTextileAndItemNameByIdTextileFormData = {
	textiles: {};
};

/**
 * @typedef {Object} getListingSmallLabelDetailsTextileFormData
 * @prop {{}} textiles - Array that contains every textile_id whose names are needed.
 */
export type getListingSmallLabelDetailsTextileFormData = {
	textiles: {};
};

/**
 * @typedef {Object} initiateTextileDigitizationTextileFormData
 * @prop {string=} organization_id - Organization id of the supplier.
 * @prop {string=} organization_textile_name - Supplier's name for the textile
 * @prop {string=} organization_textile_code - Supplier's code for the textile
 * @prop {string=} product_id - Tengiva ecosystem product id
 */
export type initiateTextileDigitizationTextileFormData = {
	organization_id?: string;
	organization_textile_name?: string;
	organization_textile_code?: string;
	product_id?: string;
};

/**
 * @typedef {Object} viewAllTextilesImagesIdsTextileFormData Optional request's data.
 * @prop {string|null=} organization_id - Organization ID
 * @prop {string|null=} textile_id - Textile ID
 * @prop {string|null=} textile_item_id - Textile Item ID
 */
export type viewAllTextilesImagesIdsTextileFormData = {
	organization_id?: string|null;
	textile_id?: string|null;
	textile_item_id?: string|null;
};

/**
 * @typedef {Object} getManyOrganizationIdTextileFormData
 * @prop {{}} textile_ids - Textile Ids
 */
export type getManyOrganizationIdTextileFormData = {
	textile_ids: {};
};

/**
 * @typedef {Object} compileShortDescriptionByIdOrCodeTextileFormData
 * @prop {{}=} textile_ids - Textile Ids
 * @prop {{}=} tengiva_textile_codes - Tengiva Textile Codes
 */
export type compileShortDescriptionByIdOrCodeTextileFormData = {
	textile_ids?: {};
	tengiva_textile_codes?: {};
};

/**
 * @typedef {Object} addTextilesItemFormData Textile item data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {Number=} tengiva_textile_item_code - Unique number that identifies a textile item within Tengiva
 * @prop {string=} organization_textile_item_name - A name given by the supplier that identifies the textile item's color
 * @prop {string=} organization_textile_item_code - A code given by the supplier that identifies the textile item's color
 * @prop {string=} reason_of_availability_id - Reason of availability's id that needs to be linked
 * @prop {boolean=} excess_stock - Does the textile item have excess_stock (1)yes | (0)no. Defaults to 0
 * @prop {string=} quality_grade - Textile item's grade of quality ('1st grade', '2nd grade', or 'Unknown'). Defaults to Unknown
 * @prop {Number=} status - Textile item's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 0)
 * @prop {string|null=} activation_date - When was this textile item switched to be active?
 * @prop {boolean=} visibility - Textile item's visibility status (0 Or 1, 0:Private, 1:Public, Defaults to 0)
 */
export type addTextilesItemFormData = {
	textile_id: string;
	tengiva_textile_item_code?: Number;
	organization_textile_item_name?: string;
	organization_textile_item_code?: string;
	reason_of_availability_id?: string;
	excess_stock?: boolean;
	quality_grade?: string;
	status?: Number;
	activation_date?: string|null;
	visibility?: boolean;
};

/**
 * @typedef {Object} editTextilesItemFormData Textile item data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {Number=} tengiva_textile_item_code - Unique number that identifies a textile item within Tengiva
 * @prop {string=} organization_textile_item_name - A name given by the supplier that identifies the textile item's color
 * @prop {string=} organization_textile_item_code - A code given by the supplier that identifies the textile item's color
 * @prop {string=} reason_of_availability_id - Reason of availability's id that needs to be linked
 * @prop {boolean=} excess_stock - Does the textile item have excess_stock (1)yes | (0)no. Defaults to 0
 * @prop {string=} quality_grade - Textile item's grade of quality ('1st grade', '2nd grade', or 'Unknown') defaults to Unknown
 * @prop {Number=} status - Textile item's status (0 Or 1, 0:Hidden, 1:Active, Defaults to 0)
 * @prop {boolean=} visibility - Textile item's visibility status (0 Or 1, 0:Private, 1:Public, Defaults to 0)
 * @prop {string|null=} activation_date - When was this textile item switched to be active?
 */
export type editTextilesItemFormData = {
	textile_id?: string;
	tengiva_textile_item_code?: Number;
	organization_textile_item_name?: string;
	organization_textile_item_code?: string;
	reason_of_availability_id?: string;
	excess_stock?: boolean;
	quality_grade?: string;
	status?: Number;
	visibility?: boolean;
	activation_date?: string|null;
};

/**
 * @typedef {Object} initiateTextileItemDigitizationTextilesItemFormData
 * @prop {string=} organization_textile_item_name - Supplier's name for the textile item
 * @prop {string=} organization_textile_item_code - Supplier's code for the textile item
 * @prop {string=} organization_id - Supplier's organization id
 * @prop {string=} textile_id - The id of the textile which this textile_item belongs to
 */
export type initiateTextileItemDigitizationTextilesItemFormData = {
	organization_textile_item_name?: string;
	organization_textile_item_code?: string;
	organization_id?: string;
	textile_id?: string;
};

/**
 * @typedef {Object} addTextilesMetaFormData Textile meta data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {Number} current_textile_version - What is the current textile version
 * @prop {string} qr_id - Qr code's id
 * @prop {string} digitization_workflow_id - Digitization workflow's that needs to be linked
 */
export type addTextilesMetaFormData = {
	textile_id: string;
	current_textile_version: Number;
	qr_id: string;
	digitization_workflow_id: string;
};

/**
 * @typedef {Object} editTextilesMetaFormData Textile meta data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {Number=} current_textile_version - What is the current textile version
 * @prop {string=} qr_id - Qr code's id
 * @prop {string=} digitization_workflow_id - Digitization workflow's that needs to be linked
 */
export type editTextilesMetaFormData = {
	textile_id?: string;
	current_textile_version?: Number;
	qr_id?: string;
	digitization_workflow_id?: string;
};

/**
 * @typedef {Object} addTextilesNoteFormData Textile note data
 * @prop {string} textile_id - Textile's id that needs to be linked
 * @prop {string} organization_id - Organization's that needs to be linked
 * @prop {string} textile_item_id - Textile item's id that needs to be linked
 * @prop {string} note - Note about the textile
 */
export type addTextilesNoteFormData = {
	textile_id: string;
	organization_id: string;
	textile_item_id: string;
	note: string;
};

/**
 * @typedef {Object} editTextilesNoteFormData Textile note data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} organization_id - Organization's that needs to be linked
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} note - Note about the textile
 */
export type editTextilesNoteFormData = {
	textile_id?: string;
	organization_id?: string;
	textile_item_id?: string;
	note?: string;
};

/**
 * @typedef {Object} addTextilesOtherNameFormData Textile other name data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string} name - Textile's other name
 */
export type addTextilesOtherNameFormData = {
	textile_id?: string;
	textile_item_id?: string;
	name: string;
};

/**
 * @typedef {Object} editTextilesOtherNameFormData Textile other name data
 * @prop {string=} textile_id - Textile's id that needs to be linked
 * @prop {string=} textile_item_id - Textile item's id that needs to be linked
 * @prop {string=} name - Textile's other name
 */
export type editTextilesOtherNameFormData = {
	textile_id?: string;
	textile_item_id?: string;
	name?: string;
};

const service = {
	name: "PM" as ServiceName,
	version: 1
};

export const usePMApi = () => {
	/**
	 * Call an endpoint on the PM
	 *
	 * Sets the service to the Request and forwards it to the TengivaApi.call() function
	 * @param {ReactiveString} endpoint - Endpoint to request
	 * @param {RequestOptions} options - Additonal options for the Request
	 * @returns {Promise<any>}
	 */
	const call = async <T = any>(endpoint: ReactiveString, options: RequestOptions) => {
		options = {
			...options,
			service
		};
		return await useTengivaApi<T>(endpoint, options);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listApplications = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/applications/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} application - Application id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewApplication = async <T = any>(application: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/applications/view`,
			[
				application
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addApplicationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addApplication = async <T = any>(formData: addApplicationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/applications/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} application - Application id.
	 * @param {editApplicationFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editApplication = async <T = any>(application: ReactiveString, formData?: editApplicationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/applications/edit`,
			[
				application
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} application - Application id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteApplication = async <T = any>(application: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/applications/delete`,
			[
				application
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationId
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const createOrganizationDefaultsCacith = async <T = any>(organizationId: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/cacith/createOrganizationDefaults`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCatalog = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/catalogs/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByOrganizationCatalog = async <T = any>(organizationID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/catalogs/listByOrganization`,
			[
				organizationID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCatalog = async <T = any>(formData: addCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/catalogs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {viewOrganizationShowroomProductsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganizationShowroomProductsCatalog = async <T = any>(formData: viewOrganizationShowroomProductsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/catalogs/viewOrganizationShowroomProducts`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} catalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCatalog = async <T = any>(catalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/catalogs/view`,
			[
				catalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} catalog
	 * @param {editCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCatalog = async <T = any>(catalog: ReactiveString, formData: editCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/catalogs/edit`,
			[
				catalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} catalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCatalog = async <T = any>(catalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/catalogs/delete`,
			[
				catalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCertification = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/certifications/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} parentID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByParentIDCertification = async <T = any>(parentID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/certifications/listByParentID`,
			[
				parentID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addCertificationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCertification = async <T = any>(formData: addCertificationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/certifications/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} certification
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCertification = async <T = any>(certification: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/certifications/view`,
			[
				certification
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} certification
	 * @param {editCertificationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCertification = async <T = any>(certification: ReactiveString, formData: editCertificationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/certifications/edit`,
			[
				certification
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} certification
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCertification = async <T = any>(certification: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/certifications/delete`,
			[
				certification
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textileID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCertificationsTextile = async <T = any>(textileID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/certificationsTextiles/list`,
			[
				textileID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addCertificationsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCertificationsTextile = async <T = any>(formData: addCertificationsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/certificationsTextiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} certificationsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCertificationsTextile = async <T = any>(certificationsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/certificationsTextiles/view`,
			[
				certificationsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View CertificationTextile by file_id field
	 * 
	 * @param {ReactiveString} fileId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByFileIdCertificationsTextile = async <T = any>(fileId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/certificationsTextiles/viewByFileId`,
			[
				fileId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} certificationsTextile
	 * @param {editCertificationsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCertificationsTextile = async <T = any>(certificationsTextile: ReactiveString, formData: editCertificationsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/certificationsTextiles/edit`,
			[
				certificationsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} certificationsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCertificationsTextile = async <T = any>(certificationsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/certificationsTextiles/delete`,
			[
				certificationsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listChemical = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/chemicals/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} parentID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByParentIDChemical = async <T = any>(parentID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/chemicals/listByParentID`,
			[
				parentID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listChemicalsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listChemicals = async <T = any>(formData?: listChemicalsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/chemicals/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} chemical - Chemical id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewChemical = async <T = any>(chemical: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/chemicals/view`,
			[
				chemical
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addChemicalFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addChemical = async <T = any>(formData: addChemicalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/chemicals/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} chemical - Chemical id.
	 * @param {editChemicalFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editChemical = async <T = any>(chemical: ReactiveString, formData?: editChemicalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/chemicals/edit`,
			[
				chemical
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} chemical - Chemical id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteChemical = async <T = any>(chemical: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/chemicals/delete`,
			[
				chemical
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textileID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByTextileIDChemicalsTextile = async <T = any>(textileID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/chemicalsTextiles/listByTextileID`,
			[
				textileID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} chemicalID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByChemicalIDChemicalsTextile = async <T = any>(chemicalID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/chemicalsTextiles/listByChemicalID`,
			[
				chemicalID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addChemicalsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addChemicalsTextile = async <T = any>(formData: addChemicalsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/chemicalsTextiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} chemicalsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewChemicalsTextile = async <T = any>(chemicalsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/chemicalsTextiles/view`,
			[
				chemicalsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} chemicalsTextile
	 * @param {editChemicalsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editChemicalsTextile = async <T = any>(chemicalsTextile: ReactiveString, formData: editChemicalsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/chemicalsTextiles/edit`,
			[
				chemicalsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} chemicalsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteChemicalsTextile = async <T = any>(chemicalsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/chemicalsTextiles/delete`,
			[
				chemicalsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listCollectionUsersFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCollectionUsers = async <T = any>(formData?: listCollectionUsersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/collection_users/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} collectionUser - Collection User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionUser = async <T = any>(collectionUser: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collection_users/view`,
			[
				collectionUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addCollectionUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCollectionUser = async <T = any>(formData: addCollectionUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/collection_users/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} collectionUser - Collection User id.
	 * @param {editCollectionUserFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCollectionUser = async <T = any>(collectionUser: ReactiveString, formData?: editCollectionUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collection_users/edit`,
			[
				collectionUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} collectionUser - Collection User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCollectionUser = async <T = any>(collectionUser: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collection_users/delete`,
			[
				collectionUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionId
	 * @param {addManyCollectionUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addManyCollectionUser = async <T = any>(collectionId: ReactiveString, formData: addManyCollectionUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collection_users/addMany`,
			[
				collectionId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List all collections
	 * 
	 * @param {listCollectionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCollection = async <T = any>(formData: listCollectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/collections/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List collections by organization_id
	 * 
	 * @param {ReactiveString} organizationID - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByOrganizationCollection = async <T = any>(organizationID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collections/listByOrganization`,
			[
				organizationID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List collections by organization_id  (used in Frontend-System)
	 * 
	 * @param {ReactiveString} organizationID - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCompleteCollectionsByOrganizationCollection = async <T = any>(organizationID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collections/listCompleteCollectionsByOrganization`,
			[
				organizationID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View collection by id
	 * 
	 * @param {ReactiveString} collection - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollection = async <T = any>(collection: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collections/view`,
			[
				collection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addCollectionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCollection = async <T = any>(formData: addCollectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/collections/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} collection - Collection id.
	 * @param {editCollectionFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCollection = async <T = any>(collection: ReactiveString, formData?: editCollectionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collections/edit`,
			[
				collection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete collection by id
	 * 
	 * @param {ReactiveString} collection - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCollection = async <T = any>(collection: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collections/delete`,
			[
				collection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View collection and related product's data by collection id
	 * 
	 * @param {ReactiveString} collection - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionCollection = async <T = any>(collection: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collections/viewCollection`,
			[
				collection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addCollectionsTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCollectionsTextileItem = async <T = any>(formData: addCollectionsTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/collectionsTextileItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionsTextileItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionsTextileItem = async <T = any>(collectionsTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/collectionsTextileItems/view`,
			[
				collectionsTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionsTextileItem
	 * @param {editCollectionsTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCollectionsTextileItem = async <T = any>(collectionsTextileItem: ReactiveString, formData: editCollectionsTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collectionsTextileItems/edit`,
			[
				collectionsTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionsTextileItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCollectionsTextileItem = async <T = any>(collectionsTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collectionsTextileItems/delete`,
			[
				collectionsTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addCollectionsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCollectionsTextile = async <T = any>(formData: addCollectionsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/collectionsTextiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addManyCollectionsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addManyCollectionsTextile = async <T = any>(formData: addManyCollectionsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/collectionsTextiles/addMany`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionsTextile = async <T = any>(collectionsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/collectionsTextiles/view`,
			[
				collectionsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionsTextile
	 * @param {editCollectionsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCollectionsTextile = async <T = any>(collectionsTextile: ReactiveString, formData: editCollectionsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collectionsTextiles/edit`,
			[
				collectionsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collectionsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCollectionsTextile = async <T = any>(collectionsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/collectionsTextiles/delete`,
			[
				collectionsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textileItemID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listColorCoordinatesTextilesItem = async <T = any>(textileItemID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/colorCoordinatesTextilesItems/list`,
			[
				textileItemID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addColorCoordinatesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addColorCoordinatesTextilesItem = async <T = any>(formData: addColorCoordinatesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/colorCoordinatesTextilesItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} colorCoordinatesTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewColorCoordinatesTextilesItem = async <T = any>(colorCoordinatesTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/colorCoordinatesTextilesItems/view`,
			[
				colorCoordinatesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} colorCoordinatesTextilesItem
	 * @param {editColorCoordinatesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editColorCoordinatesTextilesItem = async <T = any>(colorCoordinatesTextilesItem: ReactiveString, formData: editColorCoordinatesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colorCoordinatesTextilesItems/edit`,
			[
				colorCoordinatesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} colorCoordinatesTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteColorCoordinatesTextilesItem = async <T = any>(colorCoordinatesTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colorCoordinatesTextilesItems/delete`,
			[
				colorCoordinatesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listColorPatternsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listColorPatterns = async <T = any>(formData?: listColorPatternsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/colorPatterns/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} colorPattern - Color Pattern id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewColorPattern = async <T = any>(colorPattern: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/colorPatterns/view`,
			[
				colorPattern
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addColorPatternFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addColorPattern = async <T = any>(formData: addColorPatternFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/colorPatterns/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} colorPattern - Color Pattern id.
	 * @param {editColorPatternFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editColorPattern = async <T = any>(colorPattern: ReactiveString, formData?: editColorPatternFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colorPatterns/edit`,
			[
				colorPattern
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} colorPattern - Color Pattern id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteColorPattern = async <T = any>(colorPattern: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colorPatterns/delete`,
			[
				colorPattern
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listColorSpacesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listColorSpaces = async <T = any>(formData?: listColorSpacesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/color_spaces/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} colorSpace - Color Space id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewColorSpace = async <T = any>(colorSpace: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/color_spaces/view`,
			[
				colorSpace
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addColorSpaceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addColorSpace = async <T = any>(formData: addColorSpaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/color_spaces/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} colorSpace - Color Space id.
	 * @param {editColorSpaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editColorSpace = async <T = any>(colorSpace: ReactiveString, formData?: editColorSpaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/color_spaces/edit`,
			[
				colorSpace
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} colorSpace - Color Space id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteColorSpace = async <T = any>(colorSpace: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/color_spaces/delete`,
			[
				colorSpace
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listColorTypesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listColorTypes = async <T = any>(formData?: listColorTypesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/color_types/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} colorType - Color Type id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewColorType = async <T = any>(colorType: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/color_types/view`,
			[
				colorType
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addColorTypeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addColorType = async <T = any>(formData: addColorTypeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/color_types/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} colorType - Color Type id.
	 * @param {editColorTypeFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editColorType = async <T = any>(colorType: ReactiveString, formData?: editColorTypeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/color_types/edit`,
			[
				colorType
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} colorType - Color Type id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteColorType = async <T = any>(colorType: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/color_types/delete`,
			[
				colorType
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listColorsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listColors = async <T = any>(formData?: listColorsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/colors/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} color - Color id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewColor = async <T = any>(color: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/colors/view`,
			[
				color
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addColorFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addColor = async <T = any>(formData: addColorFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/colors/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} color - Color id.
	 * @param {editColorFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editColor = async <T = any>(color: ReactiveString, formData?: editColorFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colors/edit`,
			[
				color
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} color - Color id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteColor = async <T = any>(color: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colors/delete`,
			[
				color
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addColortypesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addColortypesTextilesItem = async <T = any>(formData: addColortypesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/colortypesTextilesItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} colortypesTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewColortypesTextilesItem = async <T = any>(colortypesTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/colortypesTextilesItems/view`,
			[
				colortypesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} colortypesTextilesItem
	 * @param {editColortypesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editColortypesTextilesItem = async <T = any>(colortypesTextilesItem: ReactiveString, formData: editColortypesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colortypesTextilesItems/edit`,
			[
				colortypesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} colortypesTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteColortypesTextilesItem = async <T = any>(colortypesTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/colortypesTextilesItems/delete`,
			[
				colortypesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listConstructionAddonsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listConstructionAddons = async <T = any>(formData?: listConstructionAddonsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/constructionAddons/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} constructionAddon - Construction Addon id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewConstructionAddon = async <T = any>(constructionAddon: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/constructionAddons/view`,
			[
				constructionAddon
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addConstructionAddonFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addConstructionAddon = async <T = any>(formData: addConstructionAddonFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/constructionAddons/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} constructionAddon - Construction Addon id.
	 * @param {editConstructionAddonFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editConstructionAddon = async <T = any>(constructionAddon: ReactiveString, formData?: editConstructionAddonFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/constructionAddons/edit`,
			[
				constructionAddon
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} constructionAddon - Construction Addon id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteConstructionAddon = async <T = any>(constructionAddon: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/constructionAddons/delete`,
			[
				constructionAddon
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addConstructionAddonsLayerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addConstructionAddonsLayer = async <T = any>(formData: addConstructionAddonsLayerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/constructionAddonsLayers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} constructionAddonsLayer
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewConstructionAddonsLayer = async <T = any>(constructionAddonsLayer: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/constructionAddonsLayers/view`,
			[
				constructionAddonsLayer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} constructionAddonsLayer
	 * @param {editConstructionAddonsLayerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editConstructionAddonsLayer = async <T = any>(constructionAddonsLayer: ReactiveString, formData: editConstructionAddonsLayerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/constructionAddonsLayers/edit`,
			[
				constructionAddonsLayer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} constructionAddonsLayer
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteConstructionAddonsLayer = async <T = any>(constructionAddonsLayer: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/constructionAddonsLayers/delete`,
			[
				constructionAddonsLayer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addConstructionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addConstruction = async <T = any>(formData: addConstructionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/constructions/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} construction
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewConstruction = async <T = any>(construction: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/constructions/view`,
			[
				construction
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} construction
	 * @param {editConstructionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editConstruction = async <T = any>(construction: ReactiveString, formData: editConstructionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/constructions/edit`,
			[
				construction
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} construction
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteConstruction = async <T = any>(construction: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/constructions/delete`,
			[
				construction
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listDyeMethodsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listDyeMethods = async <T = any>(formData?: listDyeMethodsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/dyeMethods/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} dyeMethod - Dye Method id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewDyeMethod = async <T = any>(dyeMethod: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/dyeMethods/view`,
			[
				dyeMethod
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addDyeMethodFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addDyeMethod = async <T = any>(formData: addDyeMethodFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/dyeMethods/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} dyeMethod - Dye Method id.
	 * @param {editDyeMethodFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editDyeMethod = async <T = any>(dyeMethod: ReactiveString, formData?: editDyeMethodFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeMethods/edit`,
			[
				dyeMethod
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} dyeMethod - Dye Method id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteDyeMethod = async <T = any>(dyeMethod: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeMethods/delete`,
			[
				dyeMethod
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addDyeMethodsTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addDyeMethodsTextilesItem = async <T = any>(formData: addDyeMethodsTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/dyeMethodsTextilesItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} dyeMethodsTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewDyeMethodsTextilesItem = async <T = any>(dyeMethodsTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/dyeMethodsTextilesItems/view`,
			[
				dyeMethodsTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} dyeMethodsTextilesItem
	 * @param {editDyeMethodsTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editDyeMethodsTextilesItem = async <T = any>(dyeMethodsTextilesItem: ReactiveString, formData: editDyeMethodsTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeMethodsTextilesItems/edit`,
			[
				dyeMethodsTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} dyeMethodsTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteDyeMethodsTextilesItem = async <T = any>(dyeMethodsTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeMethodsTextilesItems/delete`,
			[
				dyeMethodsTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listDyeStuffsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listDyeStuffs = async <T = any>(formData?: listDyeStuffsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/dyeStuffs/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} dyeStuff - Dye Stuff id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewDyeStuff = async <T = any>(dyeStuff: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/dyeStuffs/view`,
			[
				dyeStuff
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addDyeStuffFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addDyeStuff = async <T = any>(formData: addDyeStuffFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/dyeStuffs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} dyeStuff - Dye Stuff id.
	 * @param {editDyeStuffFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editDyeStuff = async <T = any>(dyeStuff: ReactiveString, formData?: editDyeStuffFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeStuffs/edit`,
			[
				dyeStuff
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} dyeStuff - Dye Stuff id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteDyeStuff = async <T = any>(dyeStuff: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeStuffs/delete`,
			[
				dyeStuff
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addDyeStuffsTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addDyeStuffsTextileItem = async <T = any>(formData: addDyeStuffsTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/dyeStuffsTextileItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} dyeStuffsTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewDyeStuffsTextileItem = async <T = any>(dyeStuffsTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/dyeStuffsTextileItems/view`,
			[
				dyeStuffsTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} dyeStuffsTextilesItem
	 * @param {editDyeStuffsTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editDyeStuffsTextileItem = async <T = any>(dyeStuffsTextilesItem: ReactiveString, formData: editDyeStuffsTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeStuffsTextileItems/edit`,
			[
				dyeStuffsTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} dyeStuffsTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteDyeStuffsTextileItem = async <T = any>(dyeStuffsTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/dyeStuffsTextileItems/delete`,
			[
				dyeStuffsTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listExistingTextileItemDefaultsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listExistingTextileItemDefaults = async <T = any>(formData?: listExistingTextileItemDefaultsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/existing_textile_item_defaults/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} existingTextileItemDefault - Existing Textile Item Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewExistingTextileItemDefault = async <T = any>(existingTextileItemDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/existing_textile_item_defaults/view`,
			[
				existingTextileItemDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addExistingTextileItemDefaultFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addExistingTextileItemDefault = async <T = any>(formData: addExistingTextileItemDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/existing_textile_item_defaults/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} existingTextileItemDefault - Existing Textile Item Default id.
	 * @param {editExistingTextileItemDefaultFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editExistingTextileItemDefault = async <T = any>(existingTextileItemDefault: ReactiveString, formData?: editExistingTextileItemDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/existing_textile_item_defaults/edit`,
			[
				existingTextileItemDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} existingTextileItemDefault - Existing Textile Item Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteExistingTextileItemDefault = async <T = any>(existingTextileItemDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/existing_textile_item_defaults/delete`,
			[
				existingTextileItemDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listExistingTextileItemsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listExistingTextileItems = async <T = any>(formData?: listExistingTextileItemsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/existing_textile_items/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} existingTextileItem - Existing Textile Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewExistingTextileItem = async <T = any>(existingTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/existing_textile_items/view`,
			[
				existingTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addExistingTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addExistingTextileItem = async <T = any>(formData: addExistingTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/existing_textile_items/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} existingTextileItem - Existing Textile Item id.
	 * @param {editExistingTextileItemFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editExistingTextileItem = async <T = any>(existingTextileItem: ReactiveString, formData?: editExistingTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/existing_textile_items/edit`,
			[
				existingTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} existingTextileItem - Existing Textile Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteExistingTextileItem = async <T = any>(existingTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/existing_textile_items/delete`,
			[
				existingTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listFiberBrandsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listFiberBrands = async <T = any>(formData: listFiberBrandsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/fiber_brands/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} fiberBrand - Fiber Brand id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewFiberBrand = async <T = any>(fiberBrand: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fiber_brands/view`,
			[
				fiberBrand
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addFiberBrandFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addFiberBrand = async <T = any>(formData: addFiberBrandFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/fiber_brands/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} fiberBrand - Fiber Brand id.
	 * @param {editFiberBrandFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editFiberBrand = async <T = any>(fiberBrand: ReactiveString, formData?: editFiberBrandFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fiber_brands/edit`,
			[
				fiberBrand
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} fiberBrand - Fiber Brand id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteFiberBrand = async <T = any>(fiberBrand: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fiber_brands/delete`,
			[
				fiberBrand
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listFibersFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listFibers = async <T = any>(formData?: listFibersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/fibers/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} fiber - Fiber id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewFiber = async <T = any>(fiber: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/fibers/view`,
			[
				fiber
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addFiberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addFiber = async <T = any>(formData: addFiberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/fibers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} fiber - Fiber id.
	 * @param {editFiberFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editFiber = async <T = any>(fiber: ReactiveString, formData?: editFiberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fibers/edit`,
			[
				fiber
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} fiber - Fiber id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteFiber = async <T = any>(fiber: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fibers/delete`,
			[
				fiber
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addFibersLayerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addFibersLayer = async <T = any>(formData: addFibersLayerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/fibersLayers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} fibersLayer
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewFibersLayer = async <T = any>(fibersLayer: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/fibersLayers/view`,
			[
				fibersLayer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} fibersLayer
	 * @param {editFibersLayerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editFibersLayer = async <T = any>(fibersLayer: ReactiveString, formData: editFibersLayerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fibersLayers/edit`,
			[
				fibersLayer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} fibersLayer
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteFibersLayer = async <T = any>(fibersLayer: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/fibersLayers/delete`,
			[
				fibersLayer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getStaticDataGeneral = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/general/getStaticData`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addImagesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addImagesTextilesItem = async <T = any>(formData: addImagesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/imagesTextilesItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} imagesTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewImagesTextilesItem = async <T = any>(imagesTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/imagesTextilesItems/view`,
			[
				imagesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} imagesTextilesItem
	 * @param {editImagesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editImagesTextilesItem = async <T = any>(imagesTextilesItem: ReactiveString, formData: editImagesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/imagesTextilesItems/edit`,
			[
				imagesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} imagesTextilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteImagesTextilesItem = async <T = any>(imagesTextilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/imagesTextilesItems/delete`,
			[
				imagesTextilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set the new image_path for existing image_id-s
	 * 
	 * @param {setImagePathImagesTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setImagePathImagesTextilesItem = async <T = any>(formData: setImagePathImagesTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/imagesTextilesItems/setImagePath`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listLayerNamesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLayerNames = async <T = any>(formData?: listLayerNamesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/layer_names/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} layerName - Layer Name id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLayerName = async <T = any>(layerName: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layer_names/view`,
			[
				layerName
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLayerNameFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLayerName = async <T = any>(formData: addLayerNameFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/layer_names/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} layerName - Layer Name id.
	 * @param {editLayerNameFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLayerName = async <T = any>(layerName: ReactiveString, formData?: editLayerNameFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layer_names/edit`,
			[
				layerName
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} layerName - Layer Name id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLayerName = async <T = any>(layerName: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layer_names/delete`,
			[
				layerName
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listLayeringTypesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLayeringTypes = async <T = any>(formData?: listLayeringTypesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/layering_types/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} layeringType - Layering Type id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLayeringType = async <T = any>(layeringType: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layering_types/view`,
			[
				layeringType
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLayeringTypeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLayeringType = async <T = any>(formData: addLayeringTypeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/layering_types/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} layeringType - Layering Type id.
	 * @param {editLayeringTypeFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLayeringType = async <T = any>(layeringType: ReactiveString, formData?: editLayeringTypeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layering_types/edit`,
			[
				layeringType
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} layeringType - Layering Type id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLayeringType = async <T = any>(layeringType: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layering_types/delete`,
			[
				layeringType
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addLayerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLayer = async <T = any>(formData: addLayerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/layers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} layer
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLayer = async <T = any>(layer: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/layers/view`,
			[
				layer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} layer
	 * @param {editLayerFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLayer = async <T = any>(layer: ReactiveString, formData: editLayerFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layers/edit`,
			[
				layer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} layer
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLayer = async <T = any>(layer: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/layers/delete`,
			[
				layer
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listLightSourcesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listLightSources = async <T = any>(formData?: listLightSourcesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/light_sources/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} lightSource - Light Source id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLightSource = async <T = any>(lightSource: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/light_sources/view`,
			[
				lightSource
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addLightSourceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLightSource = async <T = any>(formData: addLightSourceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/light_sources/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} lightSource - Light Source id.
	 * @param {editLightSourceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLightSource = async <T = any>(lightSource: ReactiveString, formData?: editLightSourceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/light_sources/edit`,
			[
				lightSource
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} lightSource - Light Source id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLightSource = async <T = any>(lightSource: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/light_sources/delete`,
			[
				lightSource
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addLogbookFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLogbook = async <T = any>(formData: addLogbookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/logbooks/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logbook
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLogbook = async <T = any>(logbook: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/logbooks/view`,
			[
				logbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logbook
	 * @param {editLogbookFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLogbook = async <T = any>(logbook: ReactiveString, formData: editLogbookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/logbooks/edit`,
			[
				logbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logbook
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLogbook = async <T = any>(logbook: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/logbooks/delete`,
			[
				logbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addLogentryFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLogentry = async <T = any>(formData: addLogentryFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/logentries/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logentry
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLogentry = async <T = any>(logentry: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/logentries/view`,
			[
				logentry
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logentry
	 * @param {editLogentryFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLogentry = async <T = any>(logentry: ReactiveString, formData: editLogentryFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/logentries/edit`,
			[
				logentry
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logentry
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLogentry = async <T = any>(logentry: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/logentries/delete`,
			[
				logentry
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addLogsheetFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addLogsheet = async <T = any>(formData: addLogsheetFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/logsheets/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logsheet
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewLogsheet = async <T = any>(logsheet: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/logsheets/view`,
			[
				logsheet
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logsheet
	 * @param {editLogsheetFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editLogsheet = async <T = any>(logsheet: ReactiveString, formData: editLogsheetFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/logsheets/edit`,
			[
				logsheet
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} logsheet
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteLogsheet = async <T = any>(logsheet: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/logsheets/delete`,
			[
				logsheet
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textile_id
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextileMarketplace = async <T = any>(textile_id: ReactiveString, formData?: string, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/viewTextile`,
			[
				textile_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platform_id
	 * @param {listProductsForMktplcMarketplaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listProductsForMktplcMarketplace = async <T = any>(platform_id: ReactiveString, formData?: listProductsForMktplcMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listProductsForMktplc`,
			[
				platform_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List Organization id's Textiles for a platform id
	 * 
	 * Will include the inventory
	 * 
	 * @param {ReactiveString} platformId - Platform UUID
	 * @param {ReactiveString} organizationId - Organization UUID
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listTextilesForOrganizationIdMarketplace = async <T = any>(platformId: ReactiveString, organizationId: ReactiveString, formData: string, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listTextilesForOrganizationId`,
			[
				platformId, 
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} collection
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionMarketplace = async <T = any>(collection: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/viewCollection`,
			[
				collection
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List marketplace product display requests
	 * 
	 * @param {ReactiveString} platformId
	 * @param {listProductsByOrganizationForPlatformIdMarketplaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listProductsByOrganizationForPlatformIdMarketplace = async <T = any>(platformId: ReactiveString, formData?: listProductsByOrganizationForPlatformIdMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listProductsByOrganizationForPlatformId`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List collection items details by organization id
	 * 
	 * @param {ReactiveString} organizationID
	 * @param {viewCollectionsItemsSummaryMarketplaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionsItemsSummaryMarketplace = async <T = any>(organizationID: ReactiveString, formData?: viewCollectionsItemsSummaryMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/viewCollectionsItemsSummary`,
			[
				organizationID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List collections for marketplaces by organization_id
	 * 
	 * @param {ReactiveString} organizationID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listByOrganizationMarketplace = async <T = any>(organizationID: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listByOrganization`,
			[
				organizationID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List moodboards by organization id
	 * 
	 * @param {ReactiveString} organizationID
	 * @param {listMoodboardsItemsSummaryMarketplaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMoodboardsItemsSummaryMarketplace = async <T = any>(organizationID: ReactiveString, formData?: listMoodboardsItemsSummaryMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listMoodboardsItemsSummary`,
			[
				organizationID
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformId
	 * @param {getApplicableFiltersMarketplaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getApplicableFiltersMarketplace = async <T = any>(platformId: ReactiveString, formData?: getApplicableFiltersMarketplaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/getApplicableFilters`,
			[
				platformId
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List unique level 1 constructions from textiles belonging to a specific platform id
	 * 
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listConstructionsbyPlatformIdMarketplace = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listConstructionsbyPlatformId`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List top 5 most common certifications from textiles belonging to a specific platform id
	 * 
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMostCommonCertificationsMarketplace = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/listMostCommonCertifications`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listMechanicalsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMechanicals = async <T = any>(formData?: listMechanicalsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/mechanicals/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} mechanical - Mechanical id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMechanical = async <T = any>(mechanical: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/mechanicals/view`,
			[
				mechanical
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addMechanicalFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addMechanical = async <T = any>(formData: addMechanicalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/mechanicals/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} mechanical - Mechanical id.
	 * @param {editMechanicalFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editMechanical = async <T = any>(mechanical: ReactiveString, formData?: editMechanicalFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/mechanicals/edit`,
			[
				mechanical
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} mechanical - Mechanical id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteMechanical = async <T = any>(mechanical: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/mechanicals/delete`,
			[
				mechanical
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addMechanicalsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addMechanicalsTextile = async <T = any>(formData: addMechanicalsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/mechanicalsTextiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} mechanicalsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMechanicalsTextile = async <T = any>(mechanicalsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/mechanicalsTextiles/view`,
			[
				mechanicalsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} mechanicalsTextile
	 * @param {editMechanicalsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editMechanicalsTextile = async <T = any>(mechanicalsTextile: ReactiveString, formData: editMechanicalsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/mechanicalsTextiles/edit`,
			[
				mechanicalsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} mechanicalsTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteMechanicalsTextile = async <T = any>(mechanicalsTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/mechanicalsTextiles/delete`,
			[
				mechanicalsTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listMoodboardItemsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMoodboardItems = async <T = any>(formData?: listMoodboardItemsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/moodboard_items/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} moodboardItem - Moodboard Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMoodboardItem = async <T = any>(moodboardItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboard_items/view`,
			[
				moodboardItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addMoodboardItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addMoodboardItem = async <T = any>(formData: addMoodboardItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/moodboard_items/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} moodboardItem - Moodboard Item id.
	 * @param {editMoodboardItemFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editMoodboardItem = async <T = any>(moodboardItem: ReactiveString, formData?: editMoodboardItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboard_items/edit`,
			[
				moodboardItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} moodboardItem - Moodboard Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteMoodboardItem = async <T = any>(moodboardItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboard_items/delete`,
			[
				moodboardItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listMoodboardUsersFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMoodboardUsers = async <T = any>(formData?: listMoodboardUsersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/moodboard_users/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} moodboardUser - Moodboard User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMoodboardUser = async <T = any>(moodboardUser: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboard_users/view`,
			[
				moodboardUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addMoodboardUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addMoodboardUser = async <T = any>(formData: addMoodboardUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/moodboard_users/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} moodboardUser - Moodboard User id.
	 * @param {editMoodboardUserFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editMoodboardUser = async <T = any>(moodboardUser: ReactiveString, formData?: editMoodboardUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboard_users/edit`,
			[
				moodboardUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} moodboardUser - Moodboard User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteMoodboardUser = async <T = any>(moodboardUser: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboard_users/delete`,
			[
				moodboardUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listMoodboardsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listMoodboards = async <T = any>(formData?: listMoodboardsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/moodboards/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} moodboard - Moodboard id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMoodboard = async <T = any>(moodboard: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboards/view`,
			[
				moodboard
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Moodboard matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {viewAllByOrganizationIdMoodboardFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdMoodboard = async <T = any>(organization_id: ReactiveString, formData?: viewAllByOrganizationIdMoodboardFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboards/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addMoodboardFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addMoodboard = async <T = any>(formData: addMoodboardFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/moodboards/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} moodboard - Moodboard id.
	 * @param {editMoodboardFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editMoodboard = async <T = any>(moodboard: ReactiveString, formData?: editMoodboardFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboards/edit`,
			[
				moodboard
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} moodboard - Moodboard id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteMoodboard = async <T = any>(moodboard: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/moodboards/delete`,
			[
				moodboard
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listNewItemColorSpaceDefaultsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNewItemColorSpaceDefaults = async <T = any>(formData?: listNewItemColorSpaceDefaultsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_item_color_space_defaults/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} newItemColorSpaceDefault - New Item Color Space Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewNewItemColorSpaceDefault = async <T = any>(newItemColorSpaceDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_item_color_space_defaults/view`,
			[
				newItemColorSpaceDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addNewItemColorSpaceDefaultFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addNewItemColorSpaceDefault = async <T = any>(formData: addNewItemColorSpaceDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_item_color_space_defaults/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} newItemColorSpaceDefault - New Item Color Space Default id.
	 * @param {editNewItemColorSpaceDefaultFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editNewItemColorSpaceDefault = async <T = any>(newItemColorSpaceDefault: ReactiveString, formData?: editNewItemColorSpaceDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_item_color_space_defaults/edit`,
			[
				newItemColorSpaceDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} newItemColorSpaceDefault - New Item Color Space Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteNewItemColorSpaceDefault = async <T = any>(newItemColorSpaceDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_item_color_space_defaults/delete`,
			[
				newItemColorSpaceDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listNewTextileItemColorSpacesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNewTextileItemColorSpaces = async <T = any>(formData?: listNewTextileItemColorSpacesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_textile_item_color_spaces/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} newTextileItemColorSpace - New Textile Item Color Space id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewNewTextileItemColorSpace = async <T = any>(newTextileItemColorSpace: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_item_color_spaces/view`,
			[
				newTextileItemColorSpace
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addNewTextileItemColorSpaceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addNewTextileItemColorSpace = async <T = any>(formData: addNewTextileItemColorSpaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_textile_item_color_spaces/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} newTextileItemColorSpace - New Textile Item Color Space id.
	 * @param {editNewTextileItemColorSpaceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editNewTextileItemColorSpace = async <T = any>(newTextileItemColorSpace: ReactiveString, formData?: editNewTextileItemColorSpaceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_item_color_spaces/edit`,
			[
				newTextileItemColorSpace
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} newTextileItemColorSpace - New Textile Item Color Space id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteNewTextileItemColorSpace = async <T = any>(newTextileItemColorSpace: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_item_color_spaces/delete`,
			[
				newTextileItemColorSpace
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listNewTextileItemDefaultsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNewTextileItemDefaults = async <T = any>(formData?: listNewTextileItemDefaultsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_textile_item_defaults/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} newTextileItemDefault - New Textile Item Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewNewTextileItemDefault = async <T = any>(newTextileItemDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_item_defaults/view`,
			[
				newTextileItemDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addNewTextileItemDefaultFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addNewTextileItemDefault = async <T = any>(formData: addNewTextileItemDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_textile_item_defaults/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} newTextileItemDefault - New Textile Item Default id.
	 * @param {editNewTextileItemDefaultFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editNewTextileItemDefault = async <T = any>(newTextileItemDefault: ReactiveString, formData?: editNewTextileItemDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_item_defaults/edit`,
			[
				newTextileItemDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} newTextileItemDefault - New Textile Item Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteNewTextileItemDefault = async <T = any>(newTextileItemDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_item_defaults/delete`,
			[
				newTextileItemDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listNewTextileItemsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNewTextileItems = async <T = any>(formData?: listNewTextileItemsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_textile_items/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} newTextileItem - New Textile Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewNewTextileItem = async <T = any>(newTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_items/view`,
			[
				newTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addNewTextileItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addNewTextileItem = async <T = any>(formData: addNewTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/new_textile_items/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} newTextileItem - New Textile Item id.
	 * @param {editNewTextileItemFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editNewTextileItem = async <T = any>(newTextileItem: ReactiveString, formData?: editNewTextileItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_items/edit`,
			[
				newTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} newTextileItem - New Textile Item id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteNewTextileItem = async <T = any>(newTextileItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/new_textile_items/delete`,
			[
				newTextileItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addOrganizationCatalogProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationCatalogProduct = async <T = any>(formData: addOrganizationCatalogProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizationCatalogProducts/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationCatalogProduct
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganizationCatalogProduct = async <T = any>(organizationCatalogProduct: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/organizationCatalogProducts/view`,
			[
				organizationCatalogProduct
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationCatalogProduct
	 * @param {editOrganizationCatalogProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOrganizationCatalogProduct = async <T = any>(organizationCatalogProduct: ReactiveString, formData: editOrganizationCatalogProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizationCatalogProducts/edit`,
			[
				organizationCatalogProduct
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationCatalogProduct
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOrganizationCatalogProduct = async <T = any>(organizationCatalogProduct: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizationCatalogProducts/delete`,
			[
				organizationCatalogProduct
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addOrganizationsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationsCatalog = async <T = any>(formData: addOrganizationsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organizationsCatalogs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrganizationsCatalog = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/organizationsCatalogs/view`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationsCatalog
	 * @param {editOrganizationsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOrganizationsCatalog = async <T = any>(organizationsCatalog: ReactiveString, formData: editOrganizationsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizationsCatalogs/edit`,
			[
				organizationsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationsCatalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOrganizationsCatalog = async <T = any>(organizationsCatalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organizationsCatalogs/delete`,
			[
				organizationsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addOriginFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrigin = async <T = any>(formData: addOriginFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/origins/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} origin
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrigin = async <T = any>(origin: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/origins/view`,
			[
				origin
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Origin by file_id
	 * 
	 * @param {ReactiveString} fileId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByFileIdOrigin = async <T = any>(fileId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/origins/viewByFileId`,
			[
				fileId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} origin
	 * @param {editOriginFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOrigin = async <T = any>(origin: ReactiveString, formData: editOriginFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/origins/edit`,
			[
				origin
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} origin
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOrigin = async <T = any>(origin: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/origins/delete`,
			[
				origin
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listParticipantMerchantsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listParticipantMerchants = async <T = any>(formData: listParticipantMerchantsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/participant_merchants/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} participantMerchant - Participant Merchant id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewParticipantMerchant = async <T = any>(participantMerchant: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/participant_merchants/view`,
			[
				participantMerchant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Participant Merchant matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdParticipantMerchant = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/participant_merchants/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addParticipantMerchantFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addParticipantMerchant = async <T = any>(formData: addParticipantMerchantFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/participant_merchants/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} participantMerchant - Participant Merchant id.
	 * @param {editParticipantMerchantFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editParticipantMerchant = async <T = any>(participantMerchant: ReactiveString, formData?: editParticipantMerchantFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/participant_merchants/edit`,
			[
				participantMerchant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} participantMerchant - Participant Merchant id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteParticipantMerchant = async <T = any>(participantMerchant: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/participant_merchants/delete`,
			[
				participantMerchant
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPerformancesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPerformances = async <T = any>(formData?: listPerformancesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/performances/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} performance - Performance id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPerformance = async <T = any>(performance: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/performances/view`,
			[
				performance
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPerformanceFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPerformance = async <T = any>(formData: addPerformanceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/performances/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} performance - Performance id.
	 * @param {editPerformanceFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPerformance = async <T = any>(performance: ReactiveString, formData?: editPerformanceFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/performances/edit`,
			[
				performance
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} performance - Performance id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePerformance = async <T = any>(performance: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/performances/delete`,
			[
				performance
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addPerformancesTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPerformancesTextile = async <T = any>(formData: addPerformancesTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/performancesTextiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} performanceTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPerformancesTextile = async <T = any>(performanceTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/performancesTextiles/view`,
			[
				performanceTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View PerformancesTextile by file_id field
	 * 
	 * @param {ReactiveString} fileId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByFileIdPerformancesTextile = async <T = any>(fileId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/performancesTextiles/viewByFileId`,
			[
				fileId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} performanceTextile
	 * @param {editPerformancesTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPerformancesTextile = async <T = any>(performanceTextile: ReactiveString, formData: editPerformancesTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/performancesTextiles/edit`,
			[
				performanceTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} performanceTextile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePerformancesTextile = async <T = any>(performanceTextile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/performancesTextiles/delete`,
			[
				performanceTextile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listPlatformCatalogProductsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listPlatformCatalogProducts = async <T = any>(formData: listPlatformCatalogProductsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_catalog_products/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} platformCatalogProduct - Platform Catalog Product id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformCatalogProduct = async <T = any>(platformCatalogProduct: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_catalog_products/view`,
			[
				platformCatalogProduct
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewCatalogProductsByPlatform method
	 * 
	 * @param {viewCatalogProductsByPlatformPlatformCatalogProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCatalogProductsByPlatformPlatformCatalogProduct = async <T = any>(formData: viewCatalogProductsByPlatformPlatformCatalogProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_catalog_products/viewCatalogProductsForPlatform`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewSalesChannelsForTextileItemId method
	 * Useage : Merchant looking at a textile item
	 * 
	 * @param {ReactiveString} textile_item_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewSalesChannelsForTextileItemIdPlatformCatalogProduct = async <T = any>(textile_item_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_catalog_products/viewSalesChannelsForTextileItemId`,
			[
				textile_item_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * viewShowroomProductByOrganizationId method
	 * 
	 * @param {viewShowroomProductByOrganizationIdPlatformCatalogProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewShowroomProductByOrganizationIdPlatformCatalogProduct = async <T = any>(formData: viewShowroomProductByOrganizationIdPlatformCatalogProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_catalog_products/viewShowroomProductByOrganizationId`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addPlatformCatalogProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformCatalogProduct = async <T = any>(formData: addPlatformCatalogProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platform_catalog_products/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} platformCatalogProduct - Platform Catalog Product id.
	 * @param {editPlatformCatalogProductFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformCatalogProduct = async <T = any>(platformCatalogProduct: ReactiveString, formData?: editPlatformCatalogProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_catalog_products/edit`,
			[
				platformCatalogProduct
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} platformCatalogProduct - Platform Catalog Product id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformCatalogProduct = async <T = any>(platformCatalogProduct: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platform_catalog_products/delete`,
			[
				platformCatalogProduct
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addPlatformsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addPlatformsCatalog = async <T = any>(formData: addPlatformsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platformsCatalogs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformsCatalog = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/view`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewRequestsPlatformsCatalog = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewRequests`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformsCatalogsTextilesPlatformsCatalog = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewPlatformsCatalogsTextiles`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformId
	 * @param {viewPlatformProductsPlatformsCatalogFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformProductsPlatformsCatalog = async <T = any>(platformId: ReactiveString, formData?: viewPlatformProductsPlatformsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewPlatformProducts`,
			[
				platformId
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View the Catalog & Products associated with a Platform (adjusted for frontend design)
	 * 
	 * Will include the inventory & pricing (based on the user requesting it)
	 * 
	 * @param {ReactiveString} platformId - Platform UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformProductsSummaryPlatformsCatalog = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewPlatformProductsSummary`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View Merchant products for a specific Platform
	 * 
	 * Will include the inventory
	 * 
	 * @param {ReactiveString} platformId - Platform UUID
	 * @param {ReactiveString} organizationId - Organization UUID
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMerchantProductsForPlatformIdPlatformsCatalog = async <T = any>(platformId: ReactiveString, organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewMerchantProductsForPlatformId`,
			[
				platformId, 
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPlatformsDisplayRequestsPlatformsCatalog = async <T = any>(platformId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewPlatformsDisplayRequests`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * addDisplayRequest method
	 * 
	 * @param {addDisplayRequestPlatformsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addDisplayRequestPlatformsCatalog = async <T = any>(formData: addDisplayRequestPlatformsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platformsCatalogs/addDisplayRequest`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addManyTextileDisplayRequestsPlatformsCatalog = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/platformsCatalogs/addManyTextileDisplayRequests`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformsCatalog
	 * @param {editPlatformsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editPlatformsCatalog = async <T = any>(platformsCatalog: ReactiveString, formData: editPlatformsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/edit`,
			[
				platformsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} platformsCatalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deletePlatformsCatalog = async <T = any>(platformsCatalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/delete`,
			[
				platformsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all merchant's textiles and textiles_items which are requested by either merchant or marketplace to be listed in different marketplaces
	 * 
	 * @param {viewMerchantDisplayRequestsPlatformsCatalogFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewMerchantDisplayRequestsPlatformsCatalog = async <T = any>(formData?: viewMerchantDisplayRequestsPlatformsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/platformsCatalogs/viewMerchantDisplayRequests`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View products by platform_id and by a list of organization_id-s
	 * It will return only the textiles_item -s (and belonging textile) which have active status
	 * Foreach organization provided in the organization_ids request body it will return platform_catalog_products under organization_platform_catalog_products key
	 * If is set count_only to true in the request body it will return counted results foreach organization_id
	 * 
	 * @param {ReactiveString} platformId - Platform UUID
	 * @param {viewProductsByPlatformIdOrganizationsIdsPlatformsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewProductsByPlatformIdOrganizationsIdsPlatformsCatalog = async <T = any>(platformId: ReactiveString, formData: viewProductsByPlatformIdOrganizationsIdsPlatformsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/platformsCatalogs/viewProductsByPlatformIdOrganizationsIds`,
			[
				platformId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addProcurementsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addProcurementsCatalog = async <T = any>(formData: addProcurementsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/procurementsCatalogs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} procurementsCatalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewProcurementsCatalog = async <T = any>(procurementsCatalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/procurementsCatalogs/view`,
			[
				procurementsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} procurementsCatalog
	 * @param {editProcurementsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editProcurementsCatalog = async <T = any>(procurementsCatalog: ReactiveString, formData: editProcurementsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/procurementsCatalogs/edit`,
			[
				procurementsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} procurementsCatalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteProcurementsCatalog = async <T = any>(procurementsCatalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/procurementsCatalogs/delete`,
			[
				procurementsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addProduct = async <T = any>(formData: addProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/products/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} product
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewProduct = async <T = any>(product: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/products/view`,
			[
				product
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} product
	 * @param {editProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editProduct = async <T = any>(product: ReactiveString, formData: editProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/products/edit`,
			[
				product
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} product
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteProduct = async <T = any>(product: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/products/delete`,
			[
				product
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {createTextileFromQueueProductFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const createTextileFromQueueProduct = async <T = any>(formData: createTextileFromQueueProductFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/products/createTextileFromQueue`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addQualityControlFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addQualityControl = async <T = any>(formData: addQualityControlFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/qualityControls/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} qualityControl
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewQualityControl = async <T = any>(qualityControl: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/qualityControls/view`,
			[
				qualityControl
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View QualityControls by file_id field
	 * 
	 * @param {ReactiveString} fileId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByFileIdQualityControl = async <T = any>(fileId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/qualityControls/viewByFileId`,
			[
				fileId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} qualityControl
	 * @param {editQualityControlFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editQualityControl = async <T = any>(qualityControl: ReactiveString, formData: editQualityControlFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/qualityControls/edit`,
			[
				qualityControl
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} qualityControl
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteQualityControl = async <T = any>(qualityControl: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/qualityControls/delete`,
			[
				qualityControl
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addRawMaterialsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addRawMaterialsCatalog = async <T = any>(formData: addRawMaterialsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/rawMaterialsCatalogs/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} rawMaterialsCatalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewRawMaterialsCatalog = async <T = any>(rawMaterialsCatalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/rawMaterialsCatalogs/view`,
			[
				rawMaterialsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} rawMaterialsCatalog
	 * @param {editRawMaterialsCatalogFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editRawMaterialsCatalog = async <T = any>(rawMaterialsCatalog: ReactiveString, formData: editRawMaterialsCatalogFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/rawMaterialsCatalogs/edit`,
			[
				rawMaterialsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} rawMaterialsCatalog
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteRawMaterialsCatalog = async <T = any>(rawMaterialsCatalog: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/rawMaterialsCatalogs/delete`,
			[
				rawMaterialsCatalog
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listReasonOfAvailabilitiesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listReasonOfAvailabilities = async <T = any>(formData?: listReasonOfAvailabilitiesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/reasonOfAvailabilities/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} reasonOfAvailability - Reason Availability id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewReasonOfAvailability = async <T = any>(reasonOfAvailability: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/reasonOfAvailabilities/view`,
			[
				reasonOfAvailability
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addReasonOfAvailabilityFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addReasonOfAvailability = async <T = any>(formData: addReasonOfAvailabilityFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/reasonOfAvailabilities/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} reasonOfAvailability - Reason Availability id.
	 * @param {editReasonOfAvailabilityFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editReasonOfAvailability = async <T = any>(reasonOfAvailability: ReactiveString, formData?: editReasonOfAvailabilityFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/reasonOfAvailabilities/edit`,
			[
				reasonOfAvailability
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} reasonOfAvailability - Reason Availability id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteReasonOfAvailability = async <T = any>(reasonOfAvailability: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/reasonOfAvailabilities/delete`,
			[
				reasonOfAvailability
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listSharedAccessUsersFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSharedAccessUsers = async <T = any>(formData?: listSharedAccessUsersFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/shared_access_users/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} sharedAccessUser - Shared Access User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewSharedAccessUser = async <T = any>(sharedAccessUser: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/shared_access_users/view`,
			[
				sharedAccessUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addSharedAccessUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSharedAccessUser = async <T = any>(formData: addSharedAccessUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/shared_access_users/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} sharedAccessUser - Shared Access User id.
	 * @param {editSharedAccessUserFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editSharedAccessUser = async <T = any>(sharedAccessUser: ReactiveString, formData?: editSharedAccessUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/shared_access_users/edit`,
			[
				sharedAccessUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} sharedAccessUser - Shared Access User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteSharedAccessUser = async <T = any>(sharedAccessUser: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/shared_access_users/delete`,
			[
				sharedAccessUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textileItemId
	 * @param {addManySharedAccessUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addManySharedAccessUser = async <T = any>(textileItemId: ReactiveString, formData: addManySharedAccessUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/shared_access_users/addMany`,
			[
				textileItemId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} sharedAccessUser
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const revokeAccessSharedAccessUser = async <T = any>(sharedAccessUser: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/shared_access_users/revokeAccess`,
			[
				sharedAccessUser
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * ListCollectionsItemsSummary endpoint
	 * 
	 * @param {ReactiveString} organizationID - The organization_id get the collection summary from
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCollectionsItemsSummaryShowroom = async <T = any>(organizationID: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/showrooms/listCollectionsItemsSummary`,
			[
				organizationID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * ViewProducts endpoint
	 * 
	 * @param {ReactiveString} platformId - The endpoint will return textile items found the platform_catalog with the specified $platformId
	 * @param {viewProductsShowroomFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewProductsShowroom = async <T = any>(platformId: ReactiveString, formData?: viewProductsShowroomFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/showrooms/viewProducts`,
			[
				platformId
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * ViewCollection endpoint
	 * 
	 * @param {ReactiveString} collectionIdentifier - UUID corresponding the collection's string corresponding the collection's slug
	 * @param {viewCollectionShowroomFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCollectionShowroom = async <T = any>(collectionIdentifier: ReactiveString, formData?: viewCollectionShowroomFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/showrooms/viewCollection`,
			[
				collectionIdentifier
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * VewTextile endpoint
	 * 
	 * @param {ReactiveString} textile_id - UUID corresponding the the textile get
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextileShowroom = async <T = any>(textile_id: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/showrooms/viewTextile`,
			[
				textile_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addOrganizationDefaultsForSupplyChainSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsForSupplyChainSubscription = async <T = any>(formData: addOrganizationDefaultsForSupplyChainSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscriptions/addOrganizationDefaultsForSupplyChain`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addOrganizationDefaultsForMarketplaceSubscriptionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationDefaultsForMarketplaceSubscription = async <T = any>(formData: addOrganizationDefaultsForMarketplaceSubscriptionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/subscriptions/addOrganizationDefaultsForMarketplace`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addTextileItemsMetaFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextileItemsMeta = async <T = any>(formData: addTextileItemsMetaFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesItemsMetas/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItemsMeta
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextileItemsMeta = async <T = any>(textilesItemsMeta: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textilesItemsMetas/view`,
			[
				textilesItemsMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItemsMeta
	 * @param {editTextileItemsMetaFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextileItemsMeta = async <T = any>(textilesItemsMeta: ReactiveString, formData: editTextileItemsMetaFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItemsMetas/edit`,
			[
				textilesItemsMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItemsMeta
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextileItemsMeta = async <T = any>(textilesItemsMeta: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItemsMetas/delete`,
			[
				textilesItemsMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listTextileProductionOrderRequirementDefaultsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listTextileProductionOrderRequirementDefaults = async <T = any>(formData?: listTextileProductionOrderRequirementDefaultsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/textile_production_order_requirement_defaults/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} textileProductionOrderRequirementDefault - Textile Production Order Requirement Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextileProductionOrderRequirementDefault = async <T = any>(textileProductionOrderRequirementDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirement_defaults/view`,
			[
				textileProductionOrderRequirementDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Textile Production Order Requirement Default matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationIdTextileProductionOrderRequirementDefault = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirement_defaults/viewForOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addTextileProductionOrderRequirementDefaultFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextileProductionOrderRequirementDefault = async <T = any>(formData: addTextileProductionOrderRequirementDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textile_production_order_requirement_defaults/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} textileProductionOrderRequirementDefault - Textile Production Order Requirement Default id.
	 * @param {editTextileProductionOrderRequirementDefaultFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextileProductionOrderRequirementDefault = async <T = any>(textileProductionOrderRequirementDefault: ReactiveString, formData?: editTextileProductionOrderRequirementDefaultFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirement_defaults/edit`,
			[
				textileProductionOrderRequirementDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} textileProductionOrderRequirementDefault - Textile Production Order Requirement Default id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextileProductionOrderRequirementDefault = async <T = any>(textileProductionOrderRequirementDefault: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirement_defaults/delete`,
			[
				textileProductionOrderRequirementDefault
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listTextileProductionOrderRequirementsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listTextileProductionOrderRequirements = async <T = any>(formData?: listTextileProductionOrderRequirementsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/textile_production_order_requirements/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} textileProductionOrderRequirement - Textile Production Order Requirement id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextileProductionOrderRequirement = async <T = any>(textileProductionOrderRequirement: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirements/view`,
			[
				textileProductionOrderRequirement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textileId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForTextileIdTextileProductionOrderRequirement = async <T = any>(textileId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirements/viewForTextileId`,
			[
				textileId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {viewForTextileIdsTextileProductionOrderRequirementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForTextileIdsTextileProductionOrderRequirement = async <T = any>(formData: viewForTextileIdsTextileProductionOrderRequirementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textile_production_order_requirements/viewForTextileIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addTextileProductionOrderRequirementFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextileProductionOrderRequirement = async <T = any>(formData: addTextileProductionOrderRequirementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textile_production_order_requirements/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} textileProductionOrderRequirement - Textile Production Order Requirement id.
	 * @param {editTextileProductionOrderRequirementFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextileProductionOrderRequirement = async <T = any>(textileProductionOrderRequirement: ReactiveString, formData?: editTextileProductionOrderRequirementFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirements/edit`,
			[
				textileProductionOrderRequirement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} textileProductionOrderRequirement - Textile Production Order Requirement id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextileProductionOrderRequirement = async <T = any>(textileProductionOrderRequirement: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textile_production_order_requirements/delete`,
			[
				textileProductionOrderRequirement
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listTextilesApplicationsFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listTextilesApplications = async <T = any>(formData: listTextilesApplicationsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles_applications/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {listCategoriesByPlatformTextilesApplicationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCategoriesByPlatformTextilesApplication = async <T = any>(formData: listCategoriesByPlatformTextilesApplicationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/textiles_applications/listCategoriesByPlatform`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} textilesApplication - Textiles Application id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextilesApplication = async <T = any>(textilesApplication: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_applications/view`,
			[
				textilesApplication
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addTextilesApplicationFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextilesApplication = async <T = any>(formData: addTextilesApplicationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles_applications/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} textilesApplication - Textiles Application id.
	 * @param {editTextilesApplicationFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextilesApplication = async <T = any>(textilesApplication: ReactiveString, formData?: editTextilesApplicationFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_applications/edit`,
			[
				textilesApplication
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} textilesApplication - Textiles Application id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextilesApplication = async <T = any>(textilesApplication: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles_applications/delete`,
			[
				textilesApplication
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextile = async <T = any>(formData: addTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View a Textile
	 * 
	 * @param {ReactiveString} textile_identifier - Textile's ID, tengiva_textile_code slug
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextile = async <T = any>(textile_identifier: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textiles/view`,
			[
				textile_identifier
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationId
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAvailableTextile = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textiles/listAvailable`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all textiles matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {viewAllByOrganizationIdTextileFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdTextile = async <T = any>(organization_id: ReactiveString, formData?: viewAllByOrganizationIdTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Re generate organization Default catalogs from Organization ID
	 * 
	 * Supports all the paginate query params:
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {regenerateOrganizationDefaultCatalogsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const regenerateOrganizationDefaultCatalogsTextile = async <T = any>(organization_id: ReactiveString, formData: regenerateOrganizationDefaultCatalogsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/regenerateOrganizationDefaultCatalogs`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List all textiles matching an Organization ID and return minimal identification details
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listIdsByOrganizationIdTextile = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/listIdsByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textile
	 * @param {editTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextile = async <T = any>(textile: ReactiveString, formData: editTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/edit`,
			[
				textile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textile
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextile = async <T = any>(textile: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/delete`,
			[
				textile
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get one or more textile's dimensions.
	 * 
	 * @param {getDimensionsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getDimensionsTextile = async <T = any>(formData: getDimensionsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles/getDimensions`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {getTextileAndItemNameByIdTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getTextileAndItemNameByIdTextile = async <T = any>(formData: getTextileAndItemNameByIdTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/textiles/getTextileAndItemNameById`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {getListingSmallLabelDetailsTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getListingSmallLabelDetailsTextile = async <T = any>(formData: getListingSmallLabelDetailsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/textiles/getListingSmallLabelDetails`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {initiateTextileDigitizationTextileFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const initiateTextileDigitizationTextile = async <T = any>(formData?: initiateTextileDigitizationTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles/initiateTextileDigitization`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all textiles and textiles_items ids which have at least one images_textiles_items record
	 * 
	 * @param {viewAllTextilesImagesIdsTextileFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllTextilesImagesIdsTextile = async <T = any>(formData?: viewAllTextilesImagesIdsTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles/viewAllTextilesImagesIds`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get many organization_ids from an array of textile_ids
	 * 
	 * @param {getManyOrganizationIdTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getManyOrganizationIdTextile = async <T = any>(formData: getManyOrganizationIdTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles/getManyOrganizationId`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {compileShortDescriptionByIdOrCodeTextileFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const compileShortDescriptionByIdOrCodeTextile = async <T = any>(formData: compileShortDescriptionByIdOrCodeTextileFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textiles/compileShortDescriptionByIdOrCode`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} organizationId
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const compileShortDescriptionByOrganizationTextile = async <T = any>(organizationId: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/compileShortDescriptionByOrganization`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List all Textiles, TextilesItems, Layers matching an Organization ID and return its names
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listNamesByOrganizationIdTextile = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textiles/listNamesByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextilesItem = async <T = any>(formData: addTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesItems/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextilesItem = async <T = any>(textilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/view`,
			[
				textilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Textile IDs for a given list of Textile Items
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const idsToTextileIdsTextilesItem = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesItems/idsToTextileIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItem
	 * @param {editTextilesItemFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextilesItem = async <T = any>(textilesItem: ReactiveString, formData: editTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/edit`,
			[
				textilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItem
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const publishTextileItemIdTextilesItem = async <T = any>(textilesItem: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/publishTextileItemId`,
			[
				textilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItem
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextilesItem = async <T = any>(textilesItem: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/delete`,
			[
				textilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesItemID
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const duplicateTextilesItem = async <T = any>(textilesItemID: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PUT",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/duplicate`,
			[
				textilesItemID
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {initiateTextileItemDigitizationTextilesItemFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const initiateTextileItemDigitizationTextilesItem = async <T = any>(formData?: initiateTextileItemDigitizationTextilesItemFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesItems/initiateTextileItemDigitization`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} tengivaCode
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getTextileItemByTengivaCodeTextilesItem = async <T = any>(tengivaCode: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/getTextileItemByTengivaCode`,
			[
				tengivaCode
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set a textile item as the textile's default item,
	 * meaning that this textile item's image will be used when showing its textile parent in the frontend.
	 * Only one textile item can be the default per parent so any sibling that were already se as the default will be unset.
	 * 
	 * @param {ReactiveString} textilesItem
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setAsDefaultItemTextilesItem = async <T = any>(textilesItem: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesItems/setAsDefaultItem`,
			[
				textilesItem
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addTextilesMetaFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextilesMeta = async <T = any>(formData: addTextilesMetaFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesMetas/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesMeta
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextilesMeta = async <T = any>(textilesMeta: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textilesMetas/view`,
			[
				textilesMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesMeta
	 * @param {editTextilesMetaFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextilesMeta = async <T = any>(textilesMeta: ReactiveString, formData: editTextilesMetaFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesMetas/edit`,
			[
				textilesMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesMeta
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextilesMeta = async <T = any>(textilesMeta: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesMetas/delete`,
			[
				textilesMeta
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addTextilesNoteFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextilesNote = async <T = any>(formData: addTextilesNoteFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesNotes/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesNote
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextilesNote = async <T = any>(textilesNote: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textilesNotes/view`,
			[
				textilesNote
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesNote
	 * @param {editTextilesNoteFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextilesNote = async <T = any>(textilesNote: ReactiveString, formData: editTextilesNoteFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesNotes/edit`,
			[
				textilesNote
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesNote
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextilesNote = async <T = any>(textilesNote: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesNotes/delete`,
			[
				textilesNote
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {addTextilesOtherNameFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addTextilesOtherName = async <T = any>(formData: addTextilesOtherNameFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/textilesOtherNames/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesOtherName
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewTextilesOtherName = async <T = any>(textilesOtherName: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/textilesOtherNames/view`,
			[
				textilesOtherName
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesOtherName
	 * @param {editTextilesOtherNameFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editTextilesOtherName = async <T = any>(textilesOtherName: ReactiveString, formData: editTextilesOtherNameFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesOtherNames/edit`,
			[
				textilesOtherName
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * @param {ReactiveString} textilesOtherName
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteTextilesOtherName = async <T = any>(textilesOtherName: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/textilesOtherNames/delete`,
			[
				textilesOtherName
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};


	return {
		call,
		listApplications,
		viewApplication,
		addApplication,
		editApplication,
		deleteApplication,
		createOrganizationDefaultsCacith,
		listCatalog,
		listByOrganizationCatalog,
		addCatalog,
		viewOrganizationShowroomProductsCatalog,
		viewCatalog,
		editCatalog,
		deleteCatalog,
		listCertification,
		listByParentIDCertification,
		addCertification,
		viewCertification,
		editCertification,
		deleteCertification,
		listCertificationsTextile,
		addCertificationsTextile,
		viewCertificationsTextile,
		viewByFileIdCertificationsTextile,
		editCertificationsTextile,
		deleteCertificationsTextile,
		listChemical,
		listByParentIDChemical,
		listChemicals,
		viewChemical,
		addChemical,
		editChemical,
		deleteChemical,
		listByTextileIDChemicalsTextile,
		listByChemicalIDChemicalsTextile,
		addChemicalsTextile,
		viewChemicalsTextile,
		editChemicalsTextile,
		deleteChemicalsTextile,
		listCollectionUsers,
		viewCollectionUser,
		addCollectionUser,
		editCollectionUser,
		deleteCollectionUser,
		addManyCollectionUser,
		listCollection,
		listByOrganizationCollection,
		listCompleteCollectionsByOrganizationCollection,
		viewCollection,
		addCollection,
		editCollection,
		deleteCollection,
		viewCollectionCollection,
		addCollectionsTextileItem,
		viewCollectionsTextileItem,
		editCollectionsTextileItem,
		deleteCollectionsTextileItem,
		addCollectionsTextile,
		addManyCollectionsTextile,
		viewCollectionsTextile,
		editCollectionsTextile,
		deleteCollectionsTextile,
		listColorCoordinatesTextilesItem,
		addColorCoordinatesTextilesItem,
		viewColorCoordinatesTextilesItem,
		editColorCoordinatesTextilesItem,
		deleteColorCoordinatesTextilesItem,
		listColorPatterns,
		viewColorPattern,
		addColorPattern,
		editColorPattern,
		deleteColorPattern,
		listColorSpaces,
		viewColorSpace,
		addColorSpace,
		editColorSpace,
		deleteColorSpace,
		listColorTypes,
		viewColorType,
		addColorType,
		editColorType,
		deleteColorType,
		listColors,
		viewColor,
		addColor,
		editColor,
		deleteColor,
		addColortypesTextilesItem,
		viewColortypesTextilesItem,
		editColortypesTextilesItem,
		deleteColortypesTextilesItem,
		listConstructionAddons,
		viewConstructionAddon,
		addConstructionAddon,
		editConstructionAddon,
		deleteConstructionAddon,
		addConstructionAddonsLayer,
		viewConstructionAddonsLayer,
		editConstructionAddonsLayer,
		deleteConstructionAddonsLayer,
		addConstruction,
		viewConstruction,
		editConstruction,
		deleteConstruction,
		listDyeMethods,
		viewDyeMethod,
		addDyeMethod,
		editDyeMethod,
		deleteDyeMethod,
		addDyeMethodsTextilesItem,
		viewDyeMethodsTextilesItem,
		editDyeMethodsTextilesItem,
		deleteDyeMethodsTextilesItem,
		listDyeStuffs,
		viewDyeStuff,
		addDyeStuff,
		editDyeStuff,
		deleteDyeStuff,
		addDyeStuffsTextileItem,
		viewDyeStuffsTextileItem,
		editDyeStuffsTextileItem,
		deleteDyeStuffsTextileItem,
		listExistingTextileItemDefaults,
		viewExistingTextileItemDefault,
		addExistingTextileItemDefault,
		editExistingTextileItemDefault,
		deleteExistingTextileItemDefault,
		listExistingTextileItems,
		viewExistingTextileItem,
		addExistingTextileItem,
		editExistingTextileItem,
		deleteExistingTextileItem,
		listFiberBrands,
		viewFiberBrand,
		addFiberBrand,
		editFiberBrand,
		deleteFiberBrand,
		listFibers,
		viewFiber,
		addFiber,
		editFiber,
		deleteFiber,
		addFibersLayer,
		viewFibersLayer,
		editFibersLayer,
		deleteFibersLayer,
		getStaticDataGeneral,
		addImagesTextilesItem,
		viewImagesTextilesItem,
		editImagesTextilesItem,
		deleteImagesTextilesItem,
		setImagePathImagesTextilesItem,
		listLayerNames,
		viewLayerName,
		addLayerName,
		editLayerName,
		deleteLayerName,
		listLayeringTypes,
		viewLayeringType,
		addLayeringType,
		editLayeringType,
		deleteLayeringType,
		addLayer,
		viewLayer,
		editLayer,
		deleteLayer,
		listLightSources,
		viewLightSource,
		addLightSource,
		editLightSource,
		deleteLightSource,
		addLogbook,
		viewLogbook,
		editLogbook,
		deleteLogbook,
		addLogentry,
		viewLogentry,
		editLogentry,
		deleteLogentry,
		addLogsheet,
		viewLogsheet,
		editLogsheet,
		deleteLogsheet,
		viewTextileMarketplace,
		listProductsForMktplcMarketplace,
		listTextilesForOrganizationIdMarketplace,
		viewCollectionMarketplace,
		listProductsByOrganizationForPlatformIdMarketplace,
		viewCollectionsItemsSummaryMarketplace,
		listByOrganizationMarketplace,
		listMoodboardsItemsSummaryMarketplace,
		getApplicableFiltersMarketplace,
		listConstructionsbyPlatformIdMarketplace,
		listMostCommonCertificationsMarketplace,
		listMechanicals,
		viewMechanical,
		addMechanical,
		editMechanical,
		deleteMechanical,
		addMechanicalsTextile,
		viewMechanicalsTextile,
		editMechanicalsTextile,
		deleteMechanicalsTextile,
		listMoodboardItems,
		viewMoodboardItem,
		addMoodboardItem,
		editMoodboardItem,
		deleteMoodboardItem,
		listMoodboardUsers,
		viewMoodboardUser,
		addMoodboardUser,
		editMoodboardUser,
		deleteMoodboardUser,
		listMoodboards,
		viewMoodboard,
		viewAllByOrganizationIdMoodboard,
		addMoodboard,
		editMoodboard,
		deleteMoodboard,
		listNewItemColorSpaceDefaults,
		viewNewItemColorSpaceDefault,
		addNewItemColorSpaceDefault,
		editNewItemColorSpaceDefault,
		deleteNewItemColorSpaceDefault,
		listNewTextileItemColorSpaces,
		viewNewTextileItemColorSpace,
		addNewTextileItemColorSpace,
		editNewTextileItemColorSpace,
		deleteNewTextileItemColorSpace,
		listNewTextileItemDefaults,
		viewNewTextileItemDefault,
		addNewTextileItemDefault,
		editNewTextileItemDefault,
		deleteNewTextileItemDefault,
		listNewTextileItems,
		viewNewTextileItem,
		addNewTextileItem,
		editNewTextileItem,
		deleteNewTextileItem,
		addOrganizationCatalogProduct,
		viewOrganizationCatalogProduct,
		editOrganizationCatalogProduct,
		deleteOrganizationCatalogProduct,
		addOrganizationsCatalog,
		viewOrganizationsCatalog,
		editOrganizationsCatalog,
		deleteOrganizationsCatalog,
		addOrigin,
		viewOrigin,
		viewByFileIdOrigin,
		editOrigin,
		deleteOrigin,
		listParticipantMerchants,
		viewParticipantMerchant,
		viewAllByOrganizationIdParticipantMerchant,
		addParticipantMerchant,
		editParticipantMerchant,
		deleteParticipantMerchant,
		listPerformances,
		viewPerformance,
		addPerformance,
		editPerformance,
		deletePerformance,
		addPerformancesTextile,
		viewPerformancesTextile,
		viewByFileIdPerformancesTextile,
		editPerformancesTextile,
		deletePerformancesTextile,
		listPlatformCatalogProducts,
		viewPlatformCatalogProduct,
		viewCatalogProductsByPlatformPlatformCatalogProduct,
		viewSalesChannelsForTextileItemIdPlatformCatalogProduct,
		viewShowroomProductByOrganizationIdPlatformCatalogProduct,
		addPlatformCatalogProduct,
		editPlatformCatalogProduct,
		deletePlatformCatalogProduct,
		addPlatformsCatalog,
		viewPlatformsCatalog,
		viewRequestsPlatformsCatalog,
		viewPlatformsCatalogsTextilesPlatformsCatalog,
		viewPlatformProductsPlatformsCatalog,
		viewPlatformProductsSummaryPlatformsCatalog,
		viewMerchantProductsForPlatformIdPlatformsCatalog,
		viewPlatformsDisplayRequestsPlatformsCatalog,
		addDisplayRequestPlatformsCatalog,
		addManyTextileDisplayRequestsPlatformsCatalog,
		editPlatformsCatalog,
		deletePlatformsCatalog,
		viewMerchantDisplayRequestsPlatformsCatalog,
		viewProductsByPlatformIdOrganizationsIdsPlatformsCatalog,
		addProcurementsCatalog,
		viewProcurementsCatalog,
		editProcurementsCatalog,
		deleteProcurementsCatalog,
		addProduct,
		viewProduct,
		editProduct,
		deleteProduct,
		createTextileFromQueueProduct,
		addQualityControl,
		viewQualityControl,
		viewByFileIdQualityControl,
		editQualityControl,
		deleteQualityControl,
		addRawMaterialsCatalog,
		viewRawMaterialsCatalog,
		editRawMaterialsCatalog,
		deleteRawMaterialsCatalog,
		listReasonOfAvailabilities,
		viewReasonOfAvailability,
		addReasonOfAvailability,
		editReasonOfAvailability,
		deleteReasonOfAvailability,
		listSharedAccessUsers,
		viewSharedAccessUser,
		addSharedAccessUser,
		editSharedAccessUser,
		deleteSharedAccessUser,
		addManySharedAccessUser,
		revokeAccessSharedAccessUser,
		listCollectionsItemsSummaryShowroom,
		viewProductsShowroom,
		viewCollectionShowroom,
		viewTextileShowroom,
		addOrganizationDefaultsForSupplyChainSubscription,
		addOrganizationDefaultsForMarketplaceSubscription,
		addTextileItemsMeta,
		viewTextileItemsMeta,
		editTextileItemsMeta,
		deleteTextileItemsMeta,
		listTextileProductionOrderRequirementDefaults,
		viewTextileProductionOrderRequirementDefault,
		viewForOrganizationIdTextileProductionOrderRequirementDefault,
		addTextileProductionOrderRequirementDefault,
		editTextileProductionOrderRequirementDefault,
		deleteTextileProductionOrderRequirementDefault,
		listTextileProductionOrderRequirements,
		viewTextileProductionOrderRequirement,
		viewForTextileIdTextileProductionOrderRequirement,
		viewForTextileIdsTextileProductionOrderRequirement,
		addTextileProductionOrderRequirement,
		editTextileProductionOrderRequirement,
		deleteTextileProductionOrderRequirement,
		listTextilesApplications,
		listCategoriesByPlatformTextilesApplication,
		viewTextilesApplication,
		addTextilesApplication,
		editTextilesApplication,
		deleteTextilesApplication,
		addTextile,
		viewTextile,
		listAvailableTextile,
		viewAllByOrganizationIdTextile,
		regenerateOrganizationDefaultCatalogsTextile,
		listIdsByOrganizationIdTextile,
		editTextile,
		deleteTextile,
		getDimensionsTextile,
		getTextileAndItemNameByIdTextile,
		getListingSmallLabelDetailsTextile,
		initiateTextileDigitizationTextile,
		viewAllTextilesImagesIdsTextile,
		getManyOrganizationIdTextile,
		compileShortDescriptionByIdOrCodeTextile,
		compileShortDescriptionByOrganizationTextile,
		listNamesByOrganizationIdTextile,
		addTextilesItem,
		viewTextilesItem,
		idsToTextileIdsTextilesItem,
		editTextilesItem,
		publishTextileItemIdTextilesItem,
		deleteTextilesItem,
		duplicateTextilesItem,
		initiateTextileItemDigitizationTextilesItem,
		getTextileItemByTengivaCodeTextilesItem,
		setAsDefaultItemTextilesItem,
		addTextilesMeta,
		viewTextilesMeta,
		editTextilesMeta,
		deleteTextilesMeta,
		addTextilesNote,
		viewTextilesNote,
		editTextilesNote,
		deleteTextilesNote,
		addTextilesOtherName,
		viewTextilesOtherName,
		editTextilesOtherName,
		deleteTextilesOtherName,
	}
};